/* ============================================================
   o kama sona — toki pona trainer data
   ------------------------------------------------------------
   The full ~120-word vocabulary, grouped into themed units, plus
   a bank of example phrases and the (pure) lesson builder.

   Word glosses and notes are original, written from general toki pona
   usage. To add a word: drop it in WORDS, then list it in a unit's
   `words` array. Everything else (lessons, matching, phrases) adapts
   automatically.
   ============================================================ */

// gloss (g) = the short English meaning shown in questions.
// note  (n) = the richer "what it really means" blurb (may contain
//             simple <b>/<i> HTML) shown after you answer.
export const WORDS = {
  // ---- U1 · core words ----
  mi: { g: "I / me / we", n: "<b>mi</b> means I, me, or we — the speaker, alone or as a group. Pronouns don't mark gender or number, so context tells you how many." },
  sina: { g: "you", n: "<b>sina</b> is you — whoever is being addressed, one listener or many. It only ever points to the audience." },
  ona: { g: "he / she / it / they", n: "<b>ona</b> covers he, she, it, and they. The one third-person pronoun, used for people, animals, and objects alike." },
  jan: { g: "person / somebody", n: "<b>jan</b> is a person or human being. As a modifier it makes things people-related, as in <i>jan pona</i>, a friend." },
  ni: { g: "this / that", n: "<b>ni</b> means this or that. Point at something nearby with it, or use it to refer back to a whole idea." },
  ijo: { g: "thing / object", n: "<b>ijo</b> is a thing or object in the broadest sense — any entity, physical or abstract. The most general noun there is." },
  toki: { g: "talk / language", n: "<b>toki</b> is speech, language, or communication. As a verb, to talk; it's also the everyday greeting." },
  nimi: { g: "word / name", n: "<b>nimi</b> means word or name. As a verb, to call something by a name." },

  // ---- U2 · qualities ----
  pona: { g: "good / simple / fix", n: "<b>pona</b> means good, simple, or positive. As a verb it can mean to fix or improve — and it's the 'pona' in toki pona." },
  ike: { g: "bad / complex", n: "<b>ike</b> is bad, negative, or overly complicated. Whether something counts as ike is often a matter of opinion." },
  suli: { g: "big / important", n: "<b>suli</b> means big, tall, long, or important. Size and significance share a single word." },
  lili: { g: "small / few", n: "<b>lili</b> means small, little, or few — always relative to whatever you compare it against." },
  mute: { g: "many / very", n: "<b>mute</b> means many, much, or very. In simple counting, every amount past two is just mute." },
  ala: { g: "no / not / nothing", n: "<b>ala</b> means no, not, or nothing. Placed after a word, it negates that word." },
  sin: { g: "new / again", n: "<b>sin</b> means new or fresh, and also again. As a modifier it marks something added or repeated." },
  sama: { g: "same / like", n: "<b>sama</b> means same or similar. As a preposition, like or as." },

  // ---- U3 · colors ----
  loje: { g: "red", n: "<b>loje</b> is red — and the warm range around it, from pinkish reds to deep crimson." },
  jelo: { g: "yellow", n: "<b>jelo</b> is yellow, reaching into golden and some orange tones." },
  laso: { g: "blue / green", n: "<b>laso</b> covers both blue and green — toki pona treats them as one. Narrow it down with other words if you need to." },
  walo: { g: "white / light", n: "<b>walo</b> is white or pale — any light, washed-out shade." },
  pimeja: { g: "dark / black", n: "<b>pimeja</b> is black or dark — deep shades, shadow, and the absence of light." },
  kule: { g: "color", n: "<b>kule</b> means color or hue. As a verb, to color something; as a modifier, colorful." },

  // ---- U4 · animals ----
  soweli: { g: "land mammal", n: "<b>soweli</b> is a land animal, usually a mammal — dogs, cats, bears, and the like." },
  waso: { g: "bird", n: "<b>waso</b> is a bird, or more broadly a flying animal." },
  kala: { g: "fish / swimmer", n: "<b>kala</b> is a fish or sea creature — anything that lives and swims in water." },
  akesi: { g: "reptile / crawler", n: "<b>akesi</b> is a reptile or amphibian — lizards, snakes, frogs, and other crawling creatures." },
  pipi: { g: "bug / insect", n: "<b>pipi</b> is a bug or insect — small many-legged creatures, spiders included." },
  kijetesantakalu: { g: "weasel / raccoon-kin", n: "<b>kijetesantakalu</b> is a beloved, playful word — coined as a joke by toki pona's own creator — for raccoons and their relatives, like weasels and otters." },

  // ---- U5 · plants & food ----
  kasi: { g: "plant", n: "<b>kasi</b> is a plant — trees, flowers, grass, herbs, anything that grows from the ground." },
  kili: { g: "fruit / vegetable", n: "<b>kili</b> is fruit or a vegetable — an edible part of a plant." },
  pan: { g: "grain / bread", n: "<b>pan</b> is grain-based food — bread, rice, pasta, and other starchy staples." },
  moku: { g: "eat / food", n: "<b>moku</b> means food, and as a verb to eat or drink." },
  telo: { g: "water / liquid", n: "<b>telo</b> is water or any liquid. As a verb, to wash or to water." },
  suwi: { g: "sweet / cute", n: "<b>suwi</b> means sweet, and also cute — anything pleasant and endearing." },

  // ---- U6 · body & senses ----
  luka: { g: "hand / arm", n: "<b>luka</b> is an arm or hand. As a verb it can mean to touch or hold; it also stands for the number five." },
  noka: { g: "foot / leg / base", n: "<b>noka</b> is a leg or foot — and the lower part of anything that meets the ground." },
  uta: { g: "mouth", n: "<b>uta</b> is the mouth — tied to eating, speaking, and kissing." },
  lukin: { g: "see / look / eye", n: "<b>lukin</b> means to see, look, or watch, and an eye. As a preverb, to try to do something." },
  kute: { g: "hear / listen / ear", n: "<b>kute</b> means to hear or listen, and an ear." },
  pilin: { g: "feel / heart / sense", n: "<b>pilin</b> is feeling — both physical touch and emotion. As a verb, to feel or to think." },
  sijelo: { g: "body / substance", n: "<b>sijelo</b> is the body, or the physical material something is made of." },

  // ---- U7 · actions & preverbs ----
  pali: { g: "work / make / do", n: "<b>pali</b> means to do, make, work, or build — purposeful effort of any kind." },
  kama: { g: "come / become", n: "<b>kama</b> means to come or arrive, and to become. As a preverb, to start to happen." },
  tawa: { g: "go / move / to", n: "<b>tawa</b> means to go or move. As a preposition, to or toward — and it can mark a point of view." },
  awen: { g: "stay / keep / still", n: "<b>awen</b> means to stay, wait, or keep. As a preverb, to continue doing something." },
  jo: { g: "have / hold", n: "<b>jo</b> means to have, hold, or carry — possession in a broad sense." },
  pana: { g: "give / send / emit", n: "<b>pana</b> means to give, send, or release — to send something outward." },
  alasa: { g: "hunt / seek / try", n: "<b>alasa</b> means to hunt, gather, or search for. As a preverb, to try to." },
  ken: { g: "can / able / maybe", n: "<b>ken</b> means can or to be able — and possibility, as in 'maybe'." },
  wile: { g: "want / need / must", n: "<b>wile</b> means to want or need. As a preverb it sits before a verb: <i>wile moku</i>, to want to eat." },

  // ---- U8 · mind & heart ----
  sona: { g: "know / knowledge", n: "<b>sona</b> means knowledge and to know. As a preverb, to know how to do something." },
  olin: { g: "love / bond", n: "<b>olin</b> is deep love or affection — for a partner, family, friends, or something dear." },
  musi: { g: "fun / play / art", n: "<b>musi</b> means fun, play, and art — anything entertaining or done for enjoyment." },
  lape: { g: "sleep / rest", n: "<b>lape</b> means to sleep or rest. As a modifier, restful or asleep." },
  nasa: { g: "weird / unusual", n: "<b>nasa</b> means strange, unusual, or silly — and sometimes intoxicated. What's nasa depends on context." },
  wawa: { g: "strong / power / energy", n: "<b>wawa</b> means strong, powerful, or energetic — strength or energy of any kind." },
  mu: { g: "animal sound", n: "<b>mu</b> is any animal sound or playful noise — the all-purpose word for non-language sounds." },
  kalama: { g: "sound / noise", n: "<b>kalama</b> is sound or noise. As a verb, to make a sound." },

  // ---- U9 · world & sky ----
  ma: { g: "land / place / country", n: "<b>ma</b> is land, ground, or soil — and by extension a place, from the dirt underfoot to a whole country." },
  suno: { g: "sun / light", n: "<b>suno</b> is the sun, and light in general. Often used for daytime." },
  mun: { g: "moon / star", n: "<b>mun</b> is the moon, and by extension stars and other things in the night sky." },
  sewi: { g: "up / high / sacred", n: "<b>sewi</b> means up, above, or high — and the sacred or divine." },
  anpa: { g: "down / low / defeat", n: "<b>anpa</b> means down, below, or low — and humbleness or defeat." },
  seli: { g: "heat / fire", n: "<b>seli</b> is heat, warmth, or fire. As a verb, to heat or cook." },
  lete: { g: "cold / raw", n: "<b>lete</b> means cold or cool — and raw, as in uncooked food." },
  kon: { g: "air / spirit / meaning", n: "<b>kon</b> is air, gas, or wind — and by extension spirit, essence, or hidden meaning." },
  sike: { g: "circle / cycle / round", n: "<b>sike</b> is a circle, ball, or anything round — and a repeating cycle, like a year." },

  // ---- U10 · home & things ----
  tomo: { g: "house / room / building", n: "<b>tomo</b> is a building, house, or room — an indoor space made for people." },
  ilo: { g: "tool / machine / device", n: "<b>ilo</b> is a tool, machine, or device — anything used to get something done." },
  poki: { g: "container / box / cup", n: "<b>poki</b> is a container — a box, cup, bowl, bag, or any vessel that holds things." },
  lipu: { g: "flat object / paper / page", n: "<b>lipu</b> is a flat, thin object — paper, a page, a leaf, a card, or a document." },
  len: { g: "cloth / clothing / hidden", n: "<b>len</b> is cloth or clothing — and by extension hiding, covering, or privacy." },
  supa: { g: "surface / table", n: "<b>supa</b> is a flat surface that holds things up — a table, shelf, floor, or bed." },
  kiwen: { g: "hard / stone / metal", n: "<b>kiwen</b> is a hard solid — stone, metal, or any rigid material." },
  ko: { g: "paste / powder / semi-solid", n: "<b>ko</b> is a soft semi-solid — paste, clay, dough, powder, or goo." },

  // ---- U11 · shapes & parts ----
  palisa: { g: "rod / stick", n: "<b>palisa</b> is a long stiff object — a rod, stick, pole, or branch." },
  linja: { g: "line / string / hair", n: "<b>linja</b> is a long flexible object — string, rope, hair, or thread." },
  leko: { g: "square / block", n: "<b>leko</b> is a square, cube, or block — and stair-like or grid shapes." },
  nena: { g: "bump / hill / nose", n: "<b>nena</b> is a bump, hill, or anything that protrudes — a nose or a button included." },
  lupa: { g: "hole / door / opening", n: "<b>lupa</b> is a hole or opening — a door, window, or any gap things pass through." },
  selo: { g: "skin / shell / outer layer", n: "<b>selo</b> is an outer layer — skin, bark, shell, or covering." },
  sinpin: { g: "front / face / wall", n: "<b>sinpin</b> is a flat vertical face — a wall, the front of something, or a person's face." },
  monsi: { g: "back / behind", n: "<b>monsi</b> is the back or rear of something, and the space behind it." },
  insa: { g: "inside / center / middle", n: "<b>insa</b> is the inside, center, or middle — the interior of anything." },
  poka: { g: "side / next to / nearby", n: "<b>poka</b> is the side of something, or the area beside it — and by extension nearness." },

  // ---- U12 · people & society ----
  mama: { g: "parent / caretaker", n: "<b>mama</b> is a parent or caregiver — anyone who creates, raises, or looks after." },
  meli: { g: "woman / feminine", n: "<b>meli</b> means woman or feminine. Its meaning is left broad and self-defined." },
  mije: { g: "man / masculine", n: "<b>mije</b> means man or masculine. Like meli, it's intentionally broad." },
  tonsi: { g: "nonbinary / gender-nonconforming", n: "<b>tonsi</b> is a community word for nonbinary and gender-nonconforming people." },
  kulupu: { g: "group / community", n: "<b>kulupu</b> is a group, community, or collection — people or things sharing something in common." },
  esun: { g: "trade / buy / sell", n: "<b>esun</b> means trade, market, or exchange — buying, selling, or swapping." },
  mani: { g: "money / wealth", n: "<b>mani</b> is money or wealth — anything used to measure value." },
  utala: { g: "fight / conflict / compete", n: "<b>utala</b> means conflict, battle, or competition — struggle of any kind, including a challenge." },
  lawa: { g: "head / lead / control", n: "<b>lawa</b> is the head, and to lead or control. As a verb, to guide or govern." },
  pakala: { g: "break / destroy / oops", n: "<b>pakala</b> means to break, damage, or mess up. On its own it works as an exclamation when things go wrong." },
  moli: { g: "death / dying", n: "<b>moli</b> means death and to die — or to be broken beyond repair." },
  jaki: { g: "gross / dirty / unpleasant", n: "<b>jaki</b> means gross, dirty, or unpleasant — anything disgusting to the senses." },

  // ---- U13 · number, time & little words ----
  wan: { g: "one / unite", n: "<b>wan</b> means one, and to be united or joined. As a verb, to combine." },
  tu: { g: "two / split", n: "<b>tu</b> means two, and to split or divide." },
  nanpa: { g: "number", n: "<b>nanpa</b> means number. It also marks ordinals: <i>nanpa wan</i>, first." },
  tenpo: { g: "time / moment", n: "<b>tenpo</b> is time — a moment, a duration, or an occasion." },
  ale: { g: "all / everything", n: "<b>ale</b> (also <i>ali</i>) means all or everything, or the whole of one thing. <i>tenpo ale</i> means always." },
  taso: { g: "only / but", n: "<b>taso</b> means only or just. At the start of a sentence, but." },
  kin: { g: "also / too / indeed", n: "<b>kin</b> means also, too, or indeed — it adds emphasis or signals 'as well'." },
  namako: { g: "extra / spice", n: "<b>namako</b> means extra or additional — a spice, a bonus, a little something more." },
  seme: { g: "what / which (question)", n: "<b>seme</b> is the question word. Put it where the unknown answer goes: <i>ni li seme?</i>, what is this?" },

  // ---- U14 · ways & connectors ----
  nasin: { g: "way / path / method", n: "<b>nasin</b> is a way, path, road, or method — a route, or a way of doing things." },
  tan: { g: "from / because / cause", n: "<b>tan</b> means from, because, or cause. As a preposition it marks a source or reason." },
  ante: { g: "different / change", n: "<b>ante</b> means different or change — another thing, or to alter something." },
  weka: { g: "away / far / remove", n: "<b>weka</b> means away, far, or absent. As a verb, to remove or get rid of." },
  open: { g: "open / start / turn on", n: "<b>open</b> means to open, begin, or start — and to turn something on." },
  pini: { g: "end / finish / close", n: "<b>pini</b> means to end, finish, or close — and to turn something off." },
  sitelen: { g: "image / writing / symbol", n: "<b>sitelen</b> is an image, drawing, or symbol — a picture or writing. As a verb, to draw or write." },
  kepeken: { g: "use / with", n: "<b>kepeken</b> means to use. As a preposition it marks the tool: <i>kepeken ilo</i>, using a tool." },
  lon: { g: "at / in / true / exist", n: "<b>lon</b> means to exist or be present — and, as a preposition, at, in, or on. It also means true or real." },
  unpa: { g: "sex / intimacy", n: "<b>unpa</b> refers to sex or intimacy. Kept brief here." },
};

// Particles handled specially in phrase questions (not vocab cards).
export const PARTICLES = ["li", "e", "la", "pi", "en", "o", "a", "anu"];

// The toki pona alphabet — fourteen letters, each mapped to exactly one sound.
// The "learn the letters" primer walks these before any vocabulary: five vowels
// then nine consonants (the order the primer lists them). `say` is the English
// "sounds like" hint; `ex`/`exEn` give a first-word example the learner meets in
// the early units; `note` flags the two letters English readers most often get
// wrong (j and w). Example words all exist in WORDS above.
export const ALPHABET = {
  vowels: [
    { l: "a", say: "“ah” — like the a in father", ex: "ala", exEn: "no / not" },
    { l: "e", say: "“eh” — like the e in bed", ex: "esun", exEn: "trade" },
    { l: "i", say: "“ee” — like the i in machine", ex: "ijo", exEn: "thing" },
    { l: "o", say: "“oh” — a pure o, like the o in “so” without the glide", ex: "ona", exEn: "he / she / it" },
    { l: "u", say: "“oo” — like the u in flute", ex: "uta", exEn: "mouth" },
  ],
  consonants: [
    { l: "j", say: "always “y” — like yes, never the English j", ex: "jan", exEn: "person", note: "The one that trips people up: <b>j</b> is the English <i>y</i>. <i>jan</i> sounds like “yan”, <i>jelo</i> like “yelo”." },
    { l: "k", say: "“k” — like kite", ex: "kili", exEn: "fruit" },
    { l: "l", say: "“l” — like love", ex: "luka", exEn: "hand" },
    { l: "m", say: "“m” — like moon", ex: "mama", exEn: "parent" },
    { l: "n", say: "“n” — like night", ex: "nimi", exEn: "word" },
    { l: "p", say: "“p” — like peace", ex: "pona", exEn: "good" },
    { l: "s", say: "“s” — like sun", ex: "suno", exEn: "sun" },
    { l: "t", say: "“t” — like top", ex: "toki", exEn: "talk" },
    { l: "w", say: "“w” — like water", ex: "waso", exEn: "bird", note: "<b>w</b> is the English <i>w</i> as in water — the very same sound, just written with one letter." },
  ],
};

// The two rules that make toki pona easy to say aloud, shown atop the primer.
export const ALPHABET_NOTE =
  "Every letter always makes the <b>same one sound</b> — toki pona is fully phonetic, so these fourteen let you pronounce any word. Stress always lands on a word's <b>first syllable</b>: <i>toki</i> is “TO-ki”, <i>pona</i> is “PO-na”.";

export const UNITS = [
  { id: "u1", title: "open la", gloss: "first words", words: ["mi", "sina", "ona", "jan", "ni", "ijo", "toki", "nimi"], lessons: 2 },
  { id: "u2", title: "pona anu ike", gloss: "qualities", words: ["pona", "ike", "suli", "lili", "mute", "ala", "sin", "sama"], lessons: 2 },
  { id: "u3", title: "kule", gloss: "the colors", words: ["loje", "jelo", "laso", "walo", "pimeja", "kule"], lessons: 2 },
  { id: "u4", title: "soweli", gloss: "the animals", words: ["soweli", "waso", "kala", "akesi", "pipi", "kijetesantakalu"], lessons: 2 },
  { id: "u5", title: "moku", gloss: "plants & food", words: ["kasi", "kili", "pan", "moku", "telo", "suwi"], lessons: 2 },
  { id: "u6", title: "sijelo", gloss: "body & senses", words: ["luka", "noka", "uta", "lukin", "kute", "pilin", "sijelo"], lessons: 2 },
  { id: "u7", title: "pali", gloss: "actions & preverbs", words: ["pali", "kama", "tawa", "awen", "jo", "pana", "alasa", "ken", "wile"], lessons: 2 },
  { id: "u8", title: "insa lawa", gloss: "mind, heart & sound", words: ["sona", "olin", "musi", "lape", "nasa", "wawa", "mu", "kalama"], lessons: 2 },
  { id: "u9", title: "ma en sewi", gloss: "world & sky", words: ["ma", "suno", "mun", "sewi", "anpa", "seli", "lete", "kon", "sike"], lessons: 2 },
  { id: "u10", title: "tomo", gloss: "home & things", words: ["tomo", "ilo", "poki", "lipu", "len", "supa", "kiwen", "ko"], lessons: 2 },
  { id: "u11", title: "sijelo ijo", gloss: "shapes & parts", words: ["palisa", "linja", "leko", "nena", "lupa", "selo", "sinpin", "monsi", "insa", "poka"], lessons: 3 },
  { id: "u12", title: "kulupu", gloss: "people & society", words: ["mama", "meli", "mije", "tonsi", "kulupu", "esun", "mani", "utala", "lawa", "pakala", "moli", "jaki"], lessons: 3 },
  { id: "u13", title: "nanpa", gloss: "number, time & little words", words: ["wan", "tu", "nanpa", "tenpo", "ale", "taso", "kin", "namako", "seme"], lessons: 2 },
  // The three prepositions (tan, kepeken, lon) lead this unit so the `prep`
  // grammar pattern can land in its first lesson and still get drilled after.
  { id: "u14", title: "nasin", gloss: "ways & connectors", words: ["tan", "kepeken", "lon", "nasin", "ante", "weka", "open", "pini", "sitelen", "unpa"], lessons: 3 },
];

// Example phrases for translation questions. The lesson builder only uses a
// phrase once every content word in it has been introduced by an earlier unit.
export const PHRASES = [
  { tp: "mi moku", en: "I eat", wrong: ["you eat", "I sleep", "I see"] },
  { tp: "sina pona", en: "you are good", wrong: ["I am good", "you are big", "you talk"] },
  { tp: "ona li suwi", en: "it is cute", wrong: ["it is big", "it is bad", "it eats"] },
  { tp: "soweli suli", en: "a big animal", wrong: ["a small animal", "a big bird", "a good animal"] },
  { tp: "kili lili", en: "a small fruit", wrong: ["a small plant", "a big fruit", "a red fruit"] },
  { tp: "jan li toki", en: "a person talks", wrong: ["a person eats", "the bird talks", "a person is good"] },
  { tp: "mi sin", en: "I am new", wrong: ["I am the same", "you are new", "I am weird"] },
  { tp: "ona li laso", en: "it is blue", wrong: ["it is red", "it is white", "it is dark"] },
  { tp: "kasi li laso", en: "the plant is green", wrong: ["the plant is red", "the bird is green", "the plant is big"] },
  { tp: "waso li moku e kili", en: "the bird eats fruit", wrong: ["the fish eats fruit", "the bird sees fruit", "the bird is a fruit"] },
  { tp: "mi lukin e soweli", en: "I see an animal", wrong: ["I hear an animal", "I eat an animal", "the animal sees me"] },
  { tp: "mi wile moku", en: "I want to eat", wrong: ["I want to sleep", "I can eat", "I like to eat"] },
  { tp: "sina ken tawa", en: "you can go", wrong: ["you must go", "you want to go", "you came"] },
  { tp: "ona li pali e tomo", en: "they build a house", wrong: ["they see a house", "they live in a house", "they break a house"] },
  { tp: "jan li pana e mani", en: "a person gives money", wrong: ["a person wants money", "a person has money", "a person takes money"] },
  { tp: "mi jo e ilo", en: "I have a tool", wrong: ["I use a tool", "I make a tool", "I want a tool"] },
  { tp: "telo li lete", en: "the water is cold", wrong: ["the water is hot", "the water is good", "the water is blue"] },
  { tp: "suno li suli", en: "the sun is big", wrong: ["the moon is big", "the sun is bright", "the sun is hot"] },
  { tp: "mi lon tomo", en: "I am at home", wrong: ["I go home", "I have a home", "I want a home"] },
  { tp: "mi tawa tomo", en: "I go home", wrong: ["I am at home", "I leave home", "I see the home"] },
  { tp: "ona li pona tawa mi", en: "I like it", wrong: ["it goes to me", "I am good to it", "it is bad to me"] },
  { tp: "mi pali kepeken luka", en: "I work with my hands", wrong: ["I see with my hands", "I have two hands", "I wash my hands"] },
  { tp: "jan mute li musi", en: "many people have fun", wrong: ["two people have fun", "many people sleep", "a person has fun"] },
  { tp: "mi olin e sina", en: "I love you", wrong: ["I see you", "I want you", "you love me"] },
  { tp: "tenpo ni la mi moku", en: "right now, I eat", wrong: ["yesterday I ate", "soon I will eat", "I want to eat now"] },
  { tp: "ona li seme", en: "what is it?", wrong: ["who are you?", "where is it?", "what do I want?"] },
  { tp: "mi sona e toki pona", en: "I know toki pona", wrong: ["I learn toki pona", "I hear toki pona", "I like toki pona"] },
  { tp: "soweli li lape", en: "the animal sleeps", wrong: ["the animal eats", "the animal eats here", "the bird sleeps"] },
  { tp: "jan pona mi li kama", en: "my friend arrives", wrong: ["my friend leaves", "my friend is good", "my friend is here"] },
  { tp: "mi wile e telo", en: "I want water", wrong: ["I drink water", "I have water", "I want food"] },

  // --- extra early-vocabulary phrases, so the first lessons aren't stuck
  //     repeating the same one or two translatable sentences ---
  // U1-only (pronouns + first nouns + particles)
  { tp: "mi jan", en: "I am a person", wrong: ["you are a person", "I talk", "they are a person"] },
  { tp: "mi toki", en: "I talk", wrong: ["you talk", "they talk", "I am a person"] },
  { tp: "sina jan", en: "you are a person", wrong: ["I am a person", "you talk", "you are a thing"] },
  { tp: "ona li jan", en: "they are a person", wrong: ["it is a thing", "they talk", "I am a person"] },
  { tp: "mi toki e nimi", en: "I say a word", wrong: ["I hear a word", "you say a word", "I know a name"] },
  { tp: "jan ni li toki", en: "this person talks", wrong: ["that person eats", "this thing talks", "a person talks"] },
  { tp: "ona li ijo", en: "it is a thing", wrong: ["it is a person", "this is a word", "they talk"] },
  { tp: "sina toki e ni", en: "you say this", wrong: ["I say this", "you hear this", "you say a word"] },
  // `alt` lists other tile orders that are equally correct toki pona for the
  // same English — build-the-sentence accepts them too. Every alt must use
  // exactly the same words as tp (the tests enforce it).
  { tp: "nimi mi li ni", en: "this is my name", alt: ["ni li nimi mi"], wrong: ["this is my language", "this is my thing", "your name is this"] },
  // U2 (qualities)
  { tp: "jan li pona", en: "the person is good", wrong: ["the person is bad", "the person is big", "the person talks"] },
  { tp: "toki sina li pona", en: "your talk is good", wrong: ["my talk is good", "your talk is bad", "your name is good"] },
  { tp: "ona li ike", en: "they are bad", wrong: ["they are good", "they are small", "it is a thing"] },
  { tp: "jan ni li suli", en: "this person is important", wrong: ["this person is small", "this person is bad", "this thing is important"] },
  { tp: "ni li pona anu ike", en: "is this good or bad?", wrong: ["this is good and bad", "this is very good", "is this big or small?"] },
  // U3 (colors)
  { tp: "ona li loje", en: "it is red", wrong: ["it is blue", "it is white", "it is dark"] },
  { tp: "kule ni li pona", en: "this color is good", wrong: ["this color is bad", "this color is new", "this thing is colorful"] },
  // U4 (animals)
  { tp: "soweli li lili", en: "the animal is small", wrong: ["the animal is big", "the bird is small", "the animal is good"] },
  { tp: "waso li laso", en: "the bird is blue", wrong: ["the bird is red", "the fish is blue", "the bird is big"] },
  // U5 (food)
  { tp: "mi moku e kili", en: "I eat fruit", wrong: ["I see fruit", "you eat fruit", "I eat bread"] },
  { tp: "pan li suwi", en: "the bread is sweet", wrong: ["the fruit is sweet", "the bread is good", "the bread is cold"] },
  { tp: "telo li pona", en: "the water is good", wrong: ["the water is bad", "the water is cold", "the food is good"] },

  // --- later-unit phrases, so every unit's lessons drill that unit's own
  //     vocabulary instead of recycling early sentences ---
  // U6 (body & senses)
  { tp: "mi kute e waso", en: "I hear a bird", wrong: ["I see a bird", "I hear an animal", "the bird hears me"] },
  { tp: "pilin mi li pona", en: "I feel good", wrong: ["I feel bad", "my body is good", "you feel good"] },
  { tp: "luka mi li suli", en: "my hand is big", wrong: ["my foot is big", "my hand is small", "my mouth is big"] },
  // U7 (actions & preverbs)
  { tp: "ona li ken kama", en: "they can come", wrong: ["they want to come", "they are coming", "they cannot come"] },
  // U8 (mind & heart)
  { tp: "kalama ni li nasa", en: "this sound is strange", wrong: ["this sound is fun", "this sound is strong", "this animal sound is loud"] },
  { tp: "mi wile lape", en: "I want to sleep", wrong: ["I want to eat", "I can sleep", "I am sleeping now"] },
  // U9 (world & sky)
  { tp: "mun li lili", en: "the moon is small", wrong: ["the sun is small", "the moon is big", "the moon is white"] },
  { tp: "kon li lete", en: "the air is cold", wrong: ["the water is cold", "the air is hot", "the fire is cold"] },
  { tp: "seli li pona tawa mi", en: "I like the warmth", wrong: ["the warmth goes to me", "I want warmth", "the cold is good to me"] },
  // U11 (shapes & parts)
  { tp: "linja ni li suli", en: "this string is long", wrong: ["this stick is long", "this string is short", "this bump is big"] },
  { tp: "insa tomo li pimeja", en: "the inside of the house is dark", wrong: ["the front of the house is dark", "the inside of the house is warm", "the outside of the house is black"] },
  { tp: "monsi mi li pilin ike", en: "my back hurts", wrong: ["my front feels good", "my back is big", "my side hurts"] },
  // U12 (people & society)
  { tp: "mama mi li pona", en: "my parent is good", wrong: ["my parent is important", "my friend is good", "my child is good"] },
  { tp: "kulupu li suli", en: "the community is big", wrong: ["the community is small", "the family is big", "the market is big"] },
  { tp: "ilo li pakala", en: "the tool is broken", wrong: ["the tool is dirty", "the tool works", "the house is broken"] },
  { tp: "telo ni li jaki", en: "this water is dirty", wrong: ["this water is clean", "this food is gross", "this water is sweet"] },
  // U13 (number, time & little words)
  { tp: "jan wan li kama", en: "one person arrives", wrong: ["two people arrive", "one person leaves", "the first person talks"] },
  { tp: "tenpo ale la mi olin e sina", en: "I always love you", wrong: ["I love you right now", "I always see you", "you always love me"] },
  { tp: "mi jo e kili tu", en: "I have two fruits", wrong: ["I have one fruit", "I eat two fruits", "I have many fruits"] },
  { tp: "sina lukin e seme", en: "what do you see?", wrong: ["what do you hear?", "who sees you?", "what do you want?"] },
  // U14 (ways & connectors)
  { tp: "nasin ni li pona tawa mi", en: "I like this way", wrong: ["this way is bad", "I go this way", "this path is long"] },
  { tp: "mi open e poki", en: "I open the container", wrong: ["I close the container", "I have a container", "I break the box"] },
  { tp: "sitelen ni li musi", en: "this picture is fun", wrong: ["this picture is strange", "this writing is good", "this picture is bad"] },
  { tp: "mi kama tan tomo", en: "I come from the house", wrong: ["I go to the house", "I am at home", "I come to the house"] },

  // U10+ (pi — regrouped modifiers)
  { tp: "tomo pi jan pona li suli", en: "the friend's house is big", wrong: ["the good house is big", "the friend is in a big house", "the good person is big"] },
  { tp: "mi wile e poki pi telo suwi", en: "I want a container of sweet water", wrong: ["I want a container of sweet food", "I have a container of water", "I want a sweet fruit"] },
  { tp: "ilo pi kalama musi li pona", en: "the musical instrument is good", wrong: ["the loud tool is broken", "music is good", "the tool makes a sound"] },

  // --- phrases for previously phrase-starved lessons ---
  { tp: "jan mute li toki", en: "many people talk", wrong: ["one person talks", "many people eat", "many people are good"] },
  { tp: "ijo ni li walo", en: "this thing is white", wrong: ["this thing is dark", "this person is white", "this thing is new"] },
  { tp: "pipi li lili", en: "the bug is small", wrong: ["the bug is big", "the bird is small", "the fish is small"] },
  { tp: "akesi li laso", en: "the reptile is green", wrong: ["the reptile is red", "the fish is green", "the bug is green"] },
  { tp: "kijetesantakalu li soweli", en: "the raccoon is an animal", wrong: ["the animal is a raccoon", "the raccoon is a bird", "the raccoon is big"] },
  { tp: "sina wawa", en: "you are strong", wrong: ["you are weird", "I am strong", "you are fun"] },
  { tp: "jan ni li nasa", en: "this person is strange", wrong: ["this person is strong", "this person is fun", "this animal is strange"] },
  { tp: "supa li suli", en: "the table is big", wrong: ["the table is small", "the house is big", "the box is big"] },
  { tp: "len mi li loje", en: "my clothes are red", wrong: ["my clothes are yellow", "your clothes are red", "my paper is red"] },
  { tp: "mi jo e len", en: "I have clothing", wrong: ["I have a container", "I make clothing", "I want clothing"] },
  { tp: "ko li walo", en: "the powder is white", wrong: ["the powder is dark", "the stone is white", "the water is white"] },
  { tp: "leko li kiwen", en: "the block is hard", wrong: ["the block is soft stuff", "the stick is hard", "the block is round"] },
  { tp: "lupa li suli", en: "the hole is big", wrong: ["the hole is small", "the wall is big", "the bump is big"] },
  { tp: "sinpin tomo li walo", en: "the wall of the house is white", wrong: ["the inside of the house is white", "the wall of the house is dark", "the back of the house is white"] },
  { tp: "meli li toki", en: "the woman talks", wrong: ["the man talks", "the woman eats", "the parent talks"] },
  { tp: "mije ni li mama", en: "this man is a parent", wrong: ["this woman is a parent", "this man is a child", "this parent is a man"] },
  { tp: "mi pini e pali mi", en: "I finish my work", wrong: ["I start my work", "I finish your work", "I do my work"] },
  // u14-1 (ante, weka, open)
  { tp: "ona li weka", en: "they are away", wrong: ["they are here", "they come", "they are different"] },
  { tp: "ni li ma ante", en: "this is a different country", alt: ["ma ante li ni"], wrong: ["this is my country", "this is a good country", "this is a new house"] },
  { tp: "ona li loje a", en: "it is so red!", wrong: ["is it red?", "it is a little red", "it is so blue!"] },
  { tp: "suno li suli a", en: "the sun is so big!", wrong: ["is the sun big?", "the sun is a little big", "the moon is so big!"] },
];

export const PARTICLE_NOTE =
  "<b>li</b>, <b>e</b>, <b>la</b> & <b>pi</b> are particles: <b>li</b> separates a subject from its verb, <b>e</b> marks the object the verb acts on, <b>la</b> sets off context at the front of a sentence, and <b>pi</b> groups the words after it into one modifier.";

/* ---------------- pure helpers ---------------- */
export const shuffle = (a) => {
  a = [...a];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const sample = (arr, n, not = []) =>
  shuffle(arr.filter((x) => !not.includes(x))).slice(0, n);

// Random integer in [min, max] inclusive — used to vary lesson length so
// repeated runs don't feel identical.
const randInt = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

// The slice of a unit's words taught in lesson `idx`. Words are split as
// evenly as possible, earlier lessons taking the remainder, so a 10-word,
// 3-lesson unit teaches 4/3/3 rather than 4/4/2 (no runt final lessons).
// This is the one split the whole module uses (buildLesson, unlockedWords,
// gameUnlockPoint), kept in one place so the lesson builder and the teach
// phase always agree on what's new.
export function lessonWords(unit, idx) {
  const base = Math.floor(unit.words.length / unit.lessons);
  const extra = unit.words.length % unit.lessons;
  const size = (i) => base + (i < extra ? 1 : 0);
  let start = 0;
  for (let i = 0; i < idx; i++) start += size(i);
  return unit.words.slice(start, start + size(idx));
}

// Example phrase that uses a given word (for the teach cards). Only phrases
// whose every content word is already taught by `lessonId` qualify, so a teach
// card never shows a sentence the learner can't parse yet; the shortest match
// wins so the example illustrates the word rather than burying it. May be
// null — most words don't appear in the phrase bank.
export const findExample = (word, lessonId) => {
  const seen = wordsThroughLesson(lessonId);
  const candidates = PHRASES.filter((p) => {
    const toks = p.tp.split(" ");
    return (
      toks.includes(word) &&
      toks.every((t) => PARTICLES.includes(t) || seen.has(t))
    );
  });
  if (!candidates.length) return null;
  return candidates.reduce((best, p) =>
    p.tp.split(" ").length < best.tp.split(" ").length ? p : best
  );
};

/* ---------------- lesson builder ---------------- */
// Builds the questions for one lesson. The recipe guarantees coverage and
// spaced repetition: every focus word is drilled twice — recognition first
// (see the word, pick its meaning), then production later (see the meaning,
// pick the word) — with a consolidating match in between and translation
// phrases at the end. Distractors prefer same-unit words so wrong answers are
// actually tempting rather than absurd.
//
// With `audio: true` (the caller checks speech support), some recognition
// happens by ear instead of by eye: up to two focus words are heard rather
// than shown, and one translation phrase becomes a "pick the transcription"
// listening question. Lesson length is unchanged either way.
export function buildLesson(unit, idx, { audio = false } = {}) {
  const focus = lessonWords(unit, idx);
  // Everything taught by the end of this lesson (its own focus words included).
  const seen = wordsThroughLesson(`${unit.id}-${idx}`);
  const allWords = Object.keys(WORDS);
  const glossOf = (w) => WORDS[w].g;

  // Distractor pools: draw from already-taught words in the same unit first
  // (semantically closer = harder), then any taught word, and only as a last
  // resort the untaught rest of the vocabulary. An untaught distractor makes
  // a question trivially easy — the right answer is the only familiar word.
  const seenArr = [...seen];
  const unitSeen = unit.words.filter((x) => seen.has(x));
  const wordDistractors = (w) => {
    const out = sample(unitSeen, 3, [w]);
    if (out.length < 3) {
      out.push(...sample(seenArr, 3 - out.length, [w, ...out]));
    }
    if (out.length < 3) {
      out.push(...sample(allWords, 3 - out.length, [w, ...out]));
    }
    return out;
  };
  const glossDistractors = (w) => {
    const seen = new Set([glossOf(w)]);
    const out = [];
    for (const x of [...shuffle(unit.words), ...shuffle(allWords)]) {
      if (out.length >= 3) break;
      const g = glossOf(x);
      if (seen.has(g)) continue;
      seen.add(g);
      out.push(g);
    }
    return out;
  };

  const meaningQ = (w) => ({
    type: "meaning",
    word: w,
    correct: glossOf(w),
    options: shuffle([glossOf(w), ...glossDistractors(w)]),
  });
  const wordQ = (w) => ({
    type: "word",
    word: w,
    correct: w,
    options: shuffle([w, ...wordDistractors(w)]),
  });

  // Match consolidates the focus words (4 pairs); top up with already-taught
  // words if the lesson focuses on fewer than four — same-unit ones first.
  const matchPool = [...focus];
  for (const w of [...shuffle(unit.words), ...shuffle([...seen])]) {
    if (matchPool.length >= 4) break;
    if (!matchPool.includes(w) && seen.has(w)) matchPool.push(w);
  }
  const mp = sample(matchPool, 4);
  const matchQ = {
    type: "match",
    pairs: mp.map((w) => ({ tp: w, en: glossOf(w) })),
  };

  // Translation phrases whose words are all introduced by now — this lesson's
  // focus words included, but nothing from later lessons — preferring ones
  // that actually use a focus word from this lesson.
  const usable = unlockedPhrases([...seen]);
  const focusSet = new Set(focus);
  const usesFocus = (p) => p.tp.split(" ").some((t) => focusSet.has(t));
  const phrasePool = [
    ...shuffle(usable.filter(usesFocus)),
    ...shuffle(usable.filter((p) => !usesFocus(p))),
  ];
  const chosenPhrases = phrasePool.slice(0, randInt(2, 3));
  const phraseQs = chosenPhrases.map((p) => ({
    type: "phrase",
    tp: p.tp,
    correct: p.en,
    options: shuffle([p.en, ...p.wrong]),
    hasParticle: /\b(li|e|la|pi)\b/.test(p.tp),
  }));

  // Recognition by ear: same options as the meaning questions, but the word
  // is spoken instead of shown. Up to two focus words are met this way.
  let meaningQs = focus.map(meaningQ);
  let listenQs = [];
  if (audio) {
    const byEar = new Set(sample(focus, Math.min(2, focus.length)));
    listenQs = [...byEar].map((w) => ({ ...meaningQ(w), type: "listen" }));
    meaningQs = meaningQs.filter((q) => !byEar.has(q.word));

    // One of the translation phrases becomes "pick what you heard" — the
    // distractors are near-mishearings, so particles matter to the ear too.
    const li = chosenPhrases.findIndex((p) => p.tp.split(" ").length >= 3);
    if (li >= 0) {
      const p = chosenPhrases[li];
      phraseQs[li] = {
        type: "listenPhrase",
        tp: p.tp,
        en: p.en,
        correct: p.tp,
        options: shuffle([p.tp, ...phraseMishearings(p.tp, [...seen])]),
      };
    }
  }

  // Assemble: recognition pass → match → production pass → phrases → grammar.
  // Content within each pass is shuffled, but recognition always precedes
  // production so you recall the word rather than copy it from a card you just
  // saw, and grammar drills land last as the lesson's "apply it" capstone.
  return [
    ...shuffle([...meaningQs, ...listenQs]),
    matchQ,
    ...shuffle(focus.map(wordQ)),
    ...phraseQs,
    ...buildGrammarDrills(unit, idx),
  ];
}

// Plausible wrong transcriptions of a spoken phrase: the same sentence with
// one particle dropped or swapped, or one content word replaced by another
// already-taught word. Used as distractors for listening questions.
export function phraseMishearings(tp, seenWords) {
  const tokens = tp.split(" ");
  const structural = ["li", "e", "la", "pi"];
  const variants = new Set();
  tokens.forEach((t, i) => {
    if (structural.includes(t)) {
      variants.add(tokens.filter((_, j) => j !== i).join(" "));
      for (const s of structural) {
        if (s !== t) {
          variants.add(tokens.map((x, j) => (j === i ? s : x)).join(" "));
        }
      }
    } else if (!PARTICLES.includes(t)) {
      for (const w of sample(seenWords, 3, [t])) {
        variants.add(tokens.map((x, j) => (j === i ? w : x)).join(" "));
      }
    }
  });
  variants.delete(tp);
  return sample([...variants], 3);
}

// Total number of lessons across all units (for the progress meter).
export const TOTAL_LESSONS = UNITS.reduce((n, u) => n + u.lessons, 0);

/* ---------------- practice games ---------------- */
// The words a learner has actually unlocked: a unit's words are split across
// its lessons (same split buildLesson uses), so a word counts as unlocked once
// the lesson that introduces it is marked complete. Returns word keys, ordered.
export function unlockedWords(completed = {}) {
  const out = [];
  UNITS.forEach((u) => {
    for (let i = 0; i < u.lessons; i++) {
      if (completed[`${u.id}-${i}`]) out.push(...lessonWords(u, i));
    }
  });
  return [...new Set(out)];
}

// Example phrases whose every content word is among the given unlocked words,
// so the sentence-builder game never asks for a word you haven't met.
export function unlockedPhrases(unlocked = []) {
  const set = new Set(unlocked);
  return PHRASES.filter((p) =>
    p.tp.split(" ").every((t) => PARTICLES.includes(t) || set.has(t))
  );
}

// Walk the curriculum in order and find the first lesson that satisfies a
// game's requirement — enough unlocked words (`min`), or at least one usable
// example phrase (`phrases`). Returns the unit/lesson position (1-based) for
// display, or null if the game is available before any lesson is finished.
export function gameUnlockPoint({ min = 1, phrases = false } = {}) {
  const seen = [];
  for (let ui = 0; ui < UNITS.length; ui++) {
    const u = UNITS[ui];
    for (let i = 0; i < u.lessons; i++) {
      seen.push(...lessonWords(u, i));
      const words = [...new Set(seen)];
      const ok = phrases
        ? unlockedPhrases(words).length > 0
        : words.length >= min;
      if (ok) return { unitNum: ui + 1, lessonNum: i + 1 };
    }
  }
  return null;
}

/* ---------------- grammar ---------------- */
// Every lesson id in curriculum order, so a grammar pattern can be "unlocked"
// once the path reaches the lesson that introduces it.
export const LESSON_ORDER = UNITS.flatMap((u) =>
  Array.from({ length: u.lessons }, (_, i) => `${u.id}-${i}`)
);

// The grammar patterns, woven into the vocab path. Each is introduced (`at`) at
// the lesson where the words to demonstrate it first exist; from then on its
// drills can appear in lessons. Examples are deliberately built from words
// unlocked by that point. `particle` (when set) is the particle a fill-in-the-
// blank drill targets for this pattern.
export const PATTERNS = [
  {
    id: "noli",
    at: "u1-1",
    title: "mi & sina — no li",
    rule: "After a bare <b>mi</b> or <b>sina</b>, the verb follows directly — there is <b>no li</b>. (A modified subject like <i>mi mute</i> still takes li.)",
    examples: [
      { tp: "mi toki", en: "I talk" },
      { tp: "sina jan", en: "you are a person" },
    ],
  },
  {
    id: "li",
    at: "u1-0",
    particle: "li",
    title: "li — subject meets verb",
    rule: "For any subject other than mi/sina, <b>li</b> separates it from its verb or description.",
    examples: [
      { tp: "ona li jan", en: "they are a person" },
    ],
  },
  {
    id: "anu",
    at: "u2-0",
    title: "anu — or",
    rule: "<b>anu</b> means or — it lays out a choice: <i>pona anu ike?</i> = good or bad? A sentence built on anu is usually already a question.",
    examples: [
      { tp: "sina pona anu ike?", en: "are you good or bad?" },
      { tp: "ona li suli anu lili?", en: "is it big or small?" },
    ],
  },
  {
    id: "mod",
    at: "u2-1",
    title: "modifiers follow",
    rule: "A describing word comes <b>after</b> the word it changes: <i>jan pona</i> = good person, never <i>pona jan</i>.",
    examples: [
      { tp: "jan pona", en: "a good person" },
      { tp: "jan suli", en: "an important person" },
      { tp: "ona li jan ike", en: "they are a bad person" },
    ],
  },
  {
    id: "poss",
    at: "u2-1",
    title: "nimi mi — yours & mine",
    rule: "There is no 'my' or 'your' — the pronouns themselves trail the noun like any other modifier: <i>nimi mi</i> = my name, <i>ijo sina</i> = your thing. No apostrophe, no extra word.",
    examples: [
      { tp: "nimi mi", en: "my name" },
      { tp: "ijo sina", en: "your thing" },
      { tp: "toki mi li pona", en: "my language is good" },
    ],
  },
  {
    id: "compound",
    at: "u2-1",
    title: "jan pona — building new words",
    rule: "toki pona has no word for friend, child, or car — you <b>build</b> them. A noun plus its describing word becomes the name of a new thing: <i>jan pona</i>, the good person, IS the word for friend. Most of the language's 'missing' vocabulary is made exactly this way.",
    examples: [
      { tp: "jan pona", en: "a friend — the good person" },
      { tp: "jan lili", en: "a child — the small person" },
      { tp: "toki pona", en: "the language itself — good, simple talk" },
    ],
  },
  {
    id: "stack",
    at: "u3-1",
    title: "modifiers stack, left to right",
    rule: "More describing words? Each new one describes <b>everything before it</b>: <i>jan pona mute</i> = (jan pona) mute — many friends, not a 'very good person'. Order changes meaning, so build outward from the noun.",
    examples: [
      { tp: "jan pona mute", en: "many friends" },
      { tp: "ijo loje lili", en: "a small red thing" },
    ],
  },
  {
    id: "neg",
    at: "u3-0",
    title: "ala — saying no",
    rule: "Put <b>ala</b> right after a word to negate it: <i>pona ala</i> = not good, <i>toki ala</i> = doesn't talk.",
    examples: [
      { tp: "mi toki ala", en: "I don't talk" },
      { tp: "ona li suli ala", en: "it isn't big" },
    ],
  },
  {
    id: "a",
    at: "u3-0",
    title: "a — emotion out loud",
    rule: "<b>a</b> adds feeling — emphasis, delight, a sigh. It attaches after a word or stands alone: <i>pona a!</i> = so good! It never changes the grammar around it.",
    examples: [
      { tp: "pona a!", en: "so good!" },
      { tp: "ona li suli a!", en: "it's so big!" },
    ],
  },
  {
    id: "ynq",
    at: "u4-0",
    title: "ala — yes / no questions",
    rule: "Ask a yes/no question by repeating the verb around <b>ala</b>: <i>sina pona ala pona?</i> = are you good?",
    examples: [
      { tp: "sina pona ala pona?", en: "are you good?" },
      { tp: "ona li suli ala suli?", en: "is it big?" },
    ],
  },
  {
    id: "e",
    at: "u1-1",
    particle: "e",
    title: "e — marking the object",
    rule: "<b>e</b> marks the thing a verb acts on: <i>mi toki e nimi</i> = I say a word.",
    examples: [
      { tp: "mi toki e nimi", en: "I say a word" },
      { tp: "sina toki e ni", en: "you say this" },
    ],
  },
  {
    id: "multi",
    at: "u5-1",
    title: "li li, e e — chaining",
    rule: "toki pona has no 'and' for verbs or objects — <b>repeat the particle</b> instead. Another li adds a verb: <i>ona li moku li toki</i> = they eat and talk. Another e adds an object: <i>mi moku e kili e pan</i> = I eat fruit and bread.",
    examples: [
      { tp: "ona li moku li toki", en: "they eat and talk" },
      { tp: "mi moku e kili e pan", en: "I eat fruit and bread" },
    ],
  },
  {
    id: "ynanswer",
    at: "u4-0",
    title: "pona. pona ala. — answering",
    rule: "There is no word for yes. Answer a question by <b>repeating its verb</b>: asked <i>sina pona ala pona?</i>, say <i>pona</i> — yes, I am — or <i>pona ala</i> — no, I'm not.",
    examples: [
      { tp: "pona", en: "yes (it is)" },
      { tp: "pona ala", en: "no (it isn't)" },
    ],
  },
  {
    id: "tawa",
    at: "u7-0",
    title: "tawa — to & for",
    rule: "As a preposition, <b>tawa</b> means to or toward — and 'from the point of view of'. That gives toki pona its way of liking things: <i>ona li pona tawa mi</i> = it is good to me = I like it.",
    examples: [
      { tp: "mi tawa sina", en: "I go to you" },
      { tp: "ona li pona tawa mi", en: "I like it" },
    ],
  },
  {
    id: "preverb",
    at: "u7-1",
    title: "wile & ken — preverbs",
    rule: "Some words slot in <b>before the verb</b> and color it: <i>wile</i> (want to), <i>ken</i> (can), <i>kama</i> (start to). Nothing comes between them and the verb — no e: <i>mi wile moku</i> = I want to eat.",
    examples: [
      { tp: "mi wile moku", en: "I want to eat" },
      { tp: "sina ken tawa", en: "you can go" },
      { tp: "mi ken pali", en: "I can work" },
    ],
  },
  {
    id: "en",
    at: "u7-1",
    title: "en — more than one subject",
    rule: "Join two subjects with <b>en</b>: <i>mi en sina</i> = you and I. Subjects only — verbs chain by repeating li, objects by repeating e.",
    examples: [
      { tp: "mi en sina li pona", en: "you and I are good" },
      { tp: "jan en soweli li moku", en: "the person and the animal eat" },
    ],
  },
  {
    id: "o",
    at: "u8-1",
    title: "o — commands & calling out",
    rule: "<b>o</b> turns a sentence into a request: <i>o moku!</i> = eat! A name + o calls to someone.",
    examples: [
      { tp: "o kama", en: "come!" },
      { tp: "sina o lape", en: "(you) sleep!" },
    ],
  },
  {
    id: "pi",
    at: "u10-0",
    particle: "pi",
    title: "pi — regrouping modifiers",
    rule: "Stacked modifiers read left to right — <b>pi</b> regroups: it makes the <i>two or more</i> words after it one modifier. <i>tomo pi jan pona</i> = a good person's house (jan pona describes tomo as one unit). Never use pi before a single word — <i>tomo jan</i> needs no pi.",
    examples: [
      { tp: "tomo pi jan pona", en: "a good person's house" },
      { tp: "ilo pi kalama musi", en: "a musical instrument" },
    ],
  },
  {
    id: "nanpa",
    at: "u13-0",
    title: "wan, tu, mute — counting",
    rule: "Numbers follow the noun like any modifier: <i>jan wan</i> = one person, <i>jan tu</i> = two, <i>jan mute</i> = many. <b>luka</b> doubles as five. <b>nanpa</b> makes ordinals: <i>nanpa wan</i> = first.",
    examples: [
      { tp: "jan tu li kama", en: "two people arrive" },
      { tp: "ni li nanpa wan", en: "this is the first one" },
    ],
  },
  {
    id: "seme",
    at: "u13-1",
    title: "seme — asking questions",
    rule: "<b>seme</b> sits exactly where the answer will go: <i>ni li seme?</i> = what is this? <i>sina lukin e seme?</i> = what do you see? <i>jan seme li toki?</i> = who is talking? And <i>anu seme</i> tacked onto a statement asks 'right? or what?'.",
    examples: [
      { tp: "ni li seme?", en: "what is this?" },
      { tp: "sina lukin e seme?", en: "what do you see?" },
      { tp: "jan seme li toki?", en: "who is talking?" },
      { tp: "sina wile moku anu seme?", en: "you want to eat, right?" },
    ],
  },
  {
    id: "la",
    at: "u13-0",
    particle: "la",
    title: "la — setting the scene",
    rule: "Whatever sits before <b>la</b> sets the context for the main clause: <i>tenpo ni la mi moku</i> = right now, I eat. A whole sentence before la turns it into if-then: <i>sina moku la sina pilin pona</i> = if you eat, you'll feel good.",
    examples: [
      { tp: "tenpo ni la mi lape", en: "right now, I sleep" },
      { tp: "tenpo pimeja la mi lape", en: "at night, I sleep" },
      { tp: "sina moku la sina pilin pona", en: "if you eat, you feel good" },
    ],
  },
  {
    id: "prep",
    at: "u14-0",
    title: "lon, kepeken, tan — the prepositions",
    rule: "Prepositions follow the verb phrase directly — no e: <b>lon</b> places (at/in), <b>kepeken</b> marks the tool (using), <b>tan</b> gives the source or reason (from / because of).",
    examples: [
      { tp: "mi lon tomo", en: "I am at home" },
      { tp: "mi pali kepeken luka", en: "I work with my hands" },
      { tp: "mi kama tan tomo", en: "I come from the house" },
    ],
  },
];

// Hand-written sentences for spot-the-error drills. Each is tagged with the
// pattern it tests (`pat`) and the content words it needs (`needs`) so it only
// appears once that grammar and vocabulary are unlocked.
export const GRAMMAR_CHECKS = [
  { tp: "mi toki", ok: true, why: "mi + verb, no li needed.", pat: "noli", needs: ["toki"] },
  { tp: "mi li toki", ok: false, why: "mi and sina never take li — just: mi toki.", pat: "noli", needs: ["toki"] },
  { tp: "sina jan", ok: true, why: "sina + noun works with no li.", pat: "noli", needs: ["jan"] },
  { tp: "sina li jan", ok: false, why: "drop the li — sina never uses it.", pat: "noli", needs: ["jan"] },
  { tp: "jan li pona", ok: true, why: "li joins the subject jan to its verb.", pat: "li", needs: ["jan", "pona"] },
  { tp: "ona li suli", ok: true, why: "li links ona to the describing word suli.", pat: "li", needs: ["suli"] },
  { tp: "ona suli", ok: false, why: "a third-person subject needs li: ona li suli.", pat: "li", needs: ["suli"] },
  { tp: "mi suli ala", ok: true, why: "ala after a word negates it.", pat: "neg", needs: ["suli", "ala"] },
  { tp: "mi ala suli", ok: false, why: "ala goes after the word it negates: mi suli ala.", pat: "neg", needs: ["suli", "ala"] },
  { tp: "mi moku e kili", ok: true, why: "e marks the object kili.", pat: "e", needs: ["moku", "kili"] },
  { tp: "mi moku kili", ok: false, why: "without e this says something else — mark the object: mi moku e kili.", pat: "e", needs: ["moku", "kili"] },
  { tp: "sina moku e telo", ok: true, why: "e marks what you drink.", pat: "e", needs: ["moku", "telo"] },
  { tp: "mi e telo", ok: false, why: "e needs a verb before it: mi moku e telo.", pat: "e", needs: ["moku", "telo"] },
  { tp: "o moku", ok: true, why: "o makes it a command: eat!", pat: "o", needs: ["moku"] },
  { tp: "o lape pona", ok: true, why: "a command with a modifier: sleep well!", pat: "o", needs: ["lape", "pona"] },
  { tp: "moku o", ok: false, why: "o comes before the command: o moku.", pat: "o", needs: ["moku"] },
  { tp: "mi en sina li pona", ok: true, why: "en joins the two subjects.", pat: "en", needs: ["pona"] },
  { tp: "tomo pi jan pona", ok: true, why: "pi groups jan pona into one modifier: a good person's house.", pat: "pi", needs: ["tomo", "jan", "pona"] },
  { tp: "ilo pi kalama musi", ok: true, why: "pi groups kalama musi: an instrument of playful sound.", pat: "pi", needs: ["ilo", "kalama", "musi"] },
  { tp: "tomo pi jan", ok: false, why: "pi needs two or more words after it — just say tomo jan.", pat: "pi", needs: ["tomo", "jan"] },
  { tp: "poki pi telo", ok: false, why: "one word after pi is wrong — poki telo already means a water container.", pat: "pi", needs: ["poki", "telo"] },
  { tp: "tenpo ni la mi lape", ok: true, why: "la sets the context before the main clause.", pat: "la", needs: ["tenpo", "lape"] },
  { tp: "mi moku la tenpo ni", ok: false, why: "the context comes before la: tenpo ni la mi moku.", pat: "la", needs: ["tenpo", "moku"] },
  { tp: "sina pona anu ike?", ok: true, why: "anu lays out the choice: good or bad?", pat: "anu", needs: ["pona", "ike"] },
  { tp: "sina pona ike anu?", ok: false, why: "anu goes between the options: pona anu ike.", pat: "anu", needs: ["pona", "ike"] },
  { tp: "mi wile moku", ok: true, why: "the preverb wile sits before the verb: I want to eat.", pat: "preverb", needs: ["wile", "moku"] },
  { tp: "mi moku wile", ok: false, why: "the preverb comes first: mi wile moku.", pat: "preverb", needs: ["wile", "moku"] },
  { tp: "sina ken toki", ok: true, why: "ken before the verb: you can talk.", pat: "preverb", needs: ["ken", "toki"] },
  { tp: "ni li pona tawa mi", ok: true, why: "tawa mi = to me: I like this.", pat: "tawa", needs: ["tawa", "pona"] },
  { tp: "jan tu li kama", ok: true, why: "the number follows the noun: two people.", pat: "nanpa", needs: ["tu", "kama"] },
  { tp: "tu jan li kama", ok: false, why: "numbers follow the noun: jan tu, not tu jan.", pat: "nanpa", needs: ["tu", "kama"] },
  { tp: "ni li seme?", ok: true, why: "seme fills the answer's slot: what is this?", pat: "seme", needs: ["ni", "seme"] },
  { tp: "seme ni li?", ok: false, why: "put seme where the answer goes: ni li seme?", pat: "seme", needs: ["ni", "seme"] },
  { tp: "mi lon tomo", ok: true, why: "lon places you: I am at home.", pat: "prep", needs: ["lon", "tomo"] },
  { tp: "mi lon e tomo", ok: false, why: "prepositions take no e: mi lon tomo.", pat: "prep", needs: ["lon", "tomo"] },
  { tp: "mi pali kepeken ilo", ok: true, why: "kepeken marks the tool being used.", pat: "prep", needs: ["pali", "kepeken", "ilo"] },
  { tp: "ona li jan", ok: true, why: "li joins the subject ona to jan.", pat: "li", needs: ["jan"] },
  { tp: "ona jan", ok: false, why: "a third-person subject needs li: ona li jan.", pat: "li", needs: ["jan"] },
  { tp: "mi toki e nimi", ok: true, why: "e marks what you say: a word.", pat: "e", needs: ["toki", "nimi"] },
  { tp: "mi e nimi", ok: false, why: "e needs a verb before it: mi toki e nimi.", pat: "e", needs: ["toki", "nimi"] },
  { tp: "jan ike li toki", ok: true, why: "ike follows jan: a bad person talks.", pat: "mod", needs: ["jan", "ike", "toki"] },
  { tp: "ike jan li toki", ok: false, why: "the describing word follows its noun: jan ike.", pat: "mod", needs: ["jan", "ike", "toki"] },
  { tp: "nimi mi li pona", ok: true, why: "mi after nimi makes it mine: my name is good.", pat: "poss", needs: ["nimi", "pona"] },
  { tp: "mi nimi li pona", ok: false, why: "the owner follows the thing owned: nimi mi, my name.", pat: "poss", needs: ["nimi", "pona"] },
  { tp: "ona li moku li toki", ok: true, why: "a second li chains a second verb: they eat and talk.", pat: "multi", needs: ["moku", "toki"] },
  { tp: "ona li moku toki", ok: false, why: "two verbs need two li: ona li moku li toki.", pat: "multi", needs: ["moku", "toki"] },
  { tp: "mi moku e kili e pan", ok: true, why: "each object gets its own e: fruit and bread.", pat: "multi", needs: ["moku", "kili", "pan"] },
  { tp: "jan lili li moku", ok: true, why: "jan lili — the small person — IS the word for child. Compounds are just noun + modifier.", pat: "compound", needs: ["jan", "lili", "moku"] },
  { tp: "ona li jan pona", ok: true, why: "jan pona, the good person: a friend. They are a friend.", pat: "compound", needs: ["jan", "pona"] },
  { tp: "ijo loje lili li pona", ok: true, why: "left to right: ((ijo loje) lili) — a small red thing.", pat: "stack", needs: ["ijo", "loje", "lili", "pona"] },
  { tp: "loje lili ijo li pona", ok: false, why: "the noun leads and the modifiers trail: ijo loje lili.", pat: "stack", needs: ["ijo", "loje", "lili", "pona"] },
  { tp: "pona a", ok: true, why: "a adds feeling: so good!", pat: "a", needs: ["pona"] },
  { tp: "ona li suli a", ok: true, why: "a after suli adds delight: it's so big!", pat: "a", needs: ["suli"] },
  { tp: "a li pona", ok: false, why: "a is a cry of feeling, not a subject — say: ona li pona a.", pat: "a", needs: ["pona"] },
  { tp: "sina pona tawa mi", ok: true, why: "tawa mi = to me: I like you.", pat: "tawa", needs: ["tawa", "pona"] },
  { tp: "mi en sina li toki", ok: true, why: "a joined subject takes li — even with mi in it.", pat: "en", needs: ["toki"] },
  { tp: "mi en sina toki", ok: false, why: "subjects joined by en take li: mi en sina li toki.", pat: "en", needs: ["toki"] },
];

// Hand-written transform drills: take a sentence and apply one operation
// (negate it, ask it as a yes/no question, add a modifier). The learner picks
// the correctly transformed sentence. Tagged by pattern + needed words for
// gating; distractors are plausible wrong transforms (misplaced ala, no change,
// modifier on the wrong side) and never use locked vocabulary.
export const TRANSFORMS = [
  // negate — ala after the word it negates
  { from: "mi moku", op: "make it negative (not)", to: "mi moku ala", wrong: ["mi ala moku", "ala mi moku", "mi moku"], pat: "neg", needs: ["moku", "ala"] },
  { from: "ona li suli", op: "make it negative (not)", to: "ona li suli ala", wrong: ["ona li ala suli", "ona ala suli", "ona li suli"], pat: "neg", needs: ["suli", "ala"] },
  { from: "sina pona", op: "make it negative (not)", to: "sina pona ala", wrong: ["sina ala pona", "ala sina pona", "sina pona pona"], pat: "neg", needs: ["pona", "ala"] },
  // yes/no question — repeat the verb around ala
  { from: "sina pona", op: "ask it as a yes/no question", to: "sina pona ala pona?", wrong: ["sina pona ala?", "sina ala pona?", "sina pona pona?"], pat: "ynq", needs: ["pona", "ala"] },
  { from: "ona li suli", op: "ask it as a yes/no question", to: "ona li suli ala suli?", wrong: ["ona li suli ala?", "ona li ala suli?", "ona suli ala suli?"], pat: "ynq", needs: ["suli", "ala"] },
  { from: "sina moku", op: "ask it as a yes/no question", to: "sina moku ala moku?", wrong: ["sina moku ala?", "sina ala moku?", "moku ala moku sina?"], pat: "ynq", needs: ["moku", "ala"] },
  // add a modifier — describing word follows the noun
  { from: "jan li moku", op: "describe the person as big (suli)", to: "jan suli li moku", wrong: ["suli jan li moku", "jan li moku suli", "jan li suli moku"], pat: "mod", needs: ["jan", "moku", "suli"] },
  { from: "mi lukin e soweli", op: "describe the animal as small (lili)", to: "mi lukin e soweli lili", wrong: ["mi lukin e lili soweli", "mi lili lukin e soweli", "mi lukin lili e soweli"], pat: "mod", needs: ["lukin", "soweli", "lili"] },
  { from: "ona li moku e kili", op: "describe the fruit as good (pona)", to: "ona li moku e kili pona", wrong: ["ona li moku e pona kili", "ona li pona moku e kili", "ona pona li moku e kili"], pat: "mod", needs: ["moku", "kili", "pona"] },
  // group a two-word modifier with pi
  { from: "poki li suli", op: "describe the container as one for sweet water (telo suwi)", to: "poki pi telo suwi li suli", wrong: ["poki telo suwi li suli", "poki pi telo li suli", "poki pi suwi telo li suli"], pat: "pi", needs: ["poki", "telo", "suwi", "suli"] },
  // turn a statement into a command with o
  { from: "sina moku", op: "turn it into a command (eat!)", to: "o moku", wrong: ["moku o", "o sina moku", "sina moku o"], pat: "o", needs: ["moku"] },
  // set the scene with la
  { from: "mi lape", op: "say it happens at night (tenpo pimeja)", to: "tenpo pimeja la mi lape", wrong: ["mi lape la tenpo pimeja", "tenpo pimeja mi lape", "mi tenpo pimeja la lape"], pat: "la", needs: ["tenpo", "pimeja", "lape"] },
  // preverbs — slot in before the verb
  { from: "mi moku", op: "say you want to do it (wile)", to: "mi wile moku", wrong: ["mi moku wile", "mi wile e moku", "wile mi moku"], pat: "preverb", needs: ["wile", "moku"] },
  { from: "ona li tawa", op: "say they are able to (ken)", to: "ona li ken tawa", wrong: ["ona li tawa ken", "ona ken li tawa", "ona li ken e tawa"], pat: "preverb", needs: ["ken", "tawa"] },
  // seme — the question word goes where the answer would
  { from: "sina lukin e ona", op: "ask what is being seen (seme)", to: "sina lukin e seme?", wrong: ["seme sina lukin e ona?", "sina seme lukin e ona?", "sina lukin seme e ona?"], pat: "seme", needs: ["seme", "lukin"] },
  { from: "jan li kama", op: "ask who is coming (seme)", to: "jan seme li kama?", wrong: ["seme jan li kama?", "jan li kama seme?", "jan li seme kama?"], pat: "seme", needs: ["seme", "jan", "kama"] },
  // group a two-word modifier following a bare noun
  { from: "jan li toki", op: "describe the person as good (pona)", to: "jan pona li toki", wrong: ["pona jan li toki", "jan li toki pona", "jan li pona toki"], pat: "mod", needs: ["jan", "toki", "pona"] },
  // build a compound — the modifier IS the new word
  { from: "mi lukin e jan", op: "make the person a friend (pona)", to: "mi lukin e jan pona", wrong: ["mi lukin e pona jan", "mi pona lukin e jan", "mi lukin pona e jan"], pat: "compound", needs: ["lukin", "jan", "pona"] },
  // stack a second modifier — it describes everything before it
  { from: "jan pona", op: "make it many (mute)", to: "jan pona mute", wrong: ["jan mute pona", "mute jan pona", "jan pona e mute"], pat: "stack", needs: ["jan", "pona", "mute"] },
  // chain a second verb / object by repeating the particle
  { from: "ona li moku", op: "say they also talk (toki)", to: "ona li moku li toki", wrong: ["ona li moku toki", "ona li moku e toki", "ona moku li toki"], pat: "multi", needs: ["moku", "toki"] },
  { from: "mi moku e kili", op: "also eat bread (pan)", to: "mi moku e kili e pan", wrong: ["mi moku e kili pan", "mi moku e kili en pan", "mi moku kili e pan"], pat: "multi", needs: ["moku", "kili", "pan"] },
  // answer a yes/no question by repeating the verb
  { from: "sina pona ala pona?", op: "answer: yes", to: "pona", wrong: ["pona ala", "ala pona", "sina pona ala pona?"], pat: "ynanswer", needs: ["pona", "ala"] },
  { from: "sina pona ala pona?", op: "answer: no", to: "pona ala", wrong: ["pona", "ala pona", "sina ala"], pat: "ynanswer", needs: ["pona", "ala"] },
  // make it someone's — the pronoun trails the noun
  { from: "soweli li suwi", op: "make the animal yours (sina)", to: "soweli sina li suwi", wrong: ["sina soweli li suwi", "soweli li sina suwi", "soweli li suwi sina"], pat: "poss", needs: ["soweli", "suwi"] },
];

const FILL_OPTIONS = ["li", "e", "la", "pi", "(none)"];

// Patterns introduced exactly at a given lesson (shown as teach cards there).
export function patternsIntroducedAt(lessonId) {
  return PATTERNS.filter((p) => p.at === lessonId);
}

// Patterns unlocked by the time the path reaches a lesson (curriculum order).
function patternsUnlockedAt(lessonId) {
  const pos = LESSON_ORDER.indexOf(lessonId);
  return PATTERNS.filter((p) => LESSON_ORDER.indexOf(p.at) <= pos);
}

// All words introduced up to and including a given lesson (in curriculum
// order). A lesson's own focus words count — they're taught in its intro
// carousel, just before the quiz runs — but words from later lessons of the
// same unit do not, so no question ever references vocabulary you haven't met.
export function wordsThroughLesson(lessonId) {
  const pos = LESSON_ORDER.indexOf(lessonId);
  const seen = new Set();
  for (let k = 0; k <= pos; k++) {
    const [uid, i] = LESSON_ORDER[k].split("-");
    const u = UNITS.find((x) => x.id === uid);
    lessonWords(u, +i).forEach((w) => seen.add(w));
  }
  return seen;
}

// Up to two grammar drills (fill-the-particle, build-the-sentence, spot-the-
// error) appropriate to a lesson's unlocked grammar and vocabulary. Appended
// after the vocab questions as the lesson's "apply it" capstone.
export function buildGrammarDrills(unit, idx) {
  const lessonId = `${unit.id}-${idx}`;
  const unlocked = patternsUnlockedAt(lessonId);
  const unlockedIds = new Set(unlocked.map((p) => p.id));
  const seen = wordsThroughLesson(lessonId);
  const phrases = unlockedPhrases([...seen]);
  const out = [];

  // fill-the-particle — blank one particle whose pattern is unlocked.
  const fillParticles = unlocked.map((p) => p.particle).filter(Boolean);
  if (fillParticles.length) {
    const cands = phrases.filter((p) =>
      p.tp.split(" ").some((t) => fillParticles.includes(t))
    );
    if (cands.length) {
      const p = sample(cands, 1)[0];
      const tokens = p.tp.split(" ");
      const spots = tokens
        .map((t, i) => (fillParticles.includes(t) ? i : -1))
        .filter((i) => i >= 0);
      const blank = sample(spots, 1)[0];
      out.push({
        type: "fill",
        tokens,
        blank,
        en: p.en,
        correct: tokens[blank],
        options: shuffle([...FILL_OPTIONS]),
      });
    }
  }

  // build-the-sentence — only once sentences have real structure (li or e).
  if (unlockedIds.has("li") || unlockedIds.has("e")) {
    const shortish = phrases.filter((p) => {
      const n = p.tp.split(" ").length;
      return n >= 2 && n <= 5;
    });
    if (shortish.length) {
      const p = sample(shortish, 1)[0];
      const tokens = p.tp.split(" ");
      // A tempting trap tile: a particle the sentence does NOT use.
      const traps = ["e", "li", "la"].filter((x) => !tokens.includes(x));
      const trap = traps.length ? sample(traps, 1)[0] : null;
      out.push({
        type: "build",
        tp: p.tp,
        en: p.en,
        correct: p.tp,
        alts: p.alt || [],
        tiles: shuffle([...tokens, ...(trap ? [trap] : [])]),
      });
    }
  }

  // spot-the-error — from the hand-written bank, gated by pattern + words.
  const spots = GRAMMAR_CHECKS.filter(
    (c) => unlockedIds.has(c.pat) && c.needs.every((w) => seen.has(w))
  );
  if (spots.length) {
    const c = sample(spots, 1)[0];
    out.push({
      type: "spot",
      tp: c.tp,
      ok: c.ok,
      why: c.why,
      correct: c.ok ? "pona" : "ike",
    });
  }

  // transform — apply one operation (negate / question / add a modifier).
  const transforms = TRANSFORMS.filter(
    (t) => unlockedIds.has(t.pat) && t.needs.every((w) => seen.has(w))
  );
  if (transforms.length) {
    const t = sample(transforms, 1)[0];
    out.push({
      type: "transform",
      from: t.from,
      op: t.op,
      correct: t.to,
      options: shuffle([t.to, ...t.wrong]),
    });
  }

  return shuffle(out).slice(0, 2);
}

/* ---------------- word stretch (unit capstones) ---------------- */
// Regular lessons drill each word's FIRST sense. But most toki pona words
// carry more — suli is big *and* important, kama is come / become / start-to,
// telo washes as a verb, and compounds like tomo tawa (car) or telo kiwen
// (ice) are whole new things. Each unit ends with a "word stretch": a
// hand-written bank of sentences that put that unit's words (and earlier
// ones, once new grammar unlocks new roles for them) into those other lives.
//
// Every item uses only vocabulary taught by the end of its unit and only
// grammar patterns introduced by then — the tests enforce the vocab half.
// `w` is the word being stretched; `note` (simple HTML) explains the sense
// after the learner answers.
export const STRETCHES = {
  u1: [
    { w: "toki", tp: "toki!", en: "hello!", wrong: ["talk!", "a language!", "a word!"], note: "<b>toki</b> is also the everyday greeting — one word opens every conversation." },
    { w: "nimi", tp: "mi nimi e ona", en: "I name them", wrong: ["I talk to them", "their name is mi", "I know their name"], note: "<b>nimi</b> as a verb: to call something by a name." },
    { w: "toki", tp: "mi toki e ni", en: "I say this", wrong: ["I see this", "this talks", "I name this"], note: "with <b>e</b>, toki takes what's said: toki e ni — to say this." },
    { w: "ijo", tp: "sina toki e ijo", en: "you talk about something", wrong: ["you make a thing", "something talks to you", "you name a person"], note: "<b>ijo</b> is anything at all — object, topic, or idea. Here it's the something being talked about." },
    { w: "jan", tp: "jan li toki", en: "somebody is talking", wrong: ["a word is spoken", "someone is listening", "people talk to me"], note: "<b>jan</b> can be indefinite — somebody, anybody. No 'a/the/some' words needed." },
  ],
  u2: [
    { w: "suli", tp: "toki ni li suli", en: "this conversation is important", wrong: ["this talk is loud", "this talk is quiet", "this word is little"], note: "<b>suli</b> is size <i>and</i> significance. An idea that is suli matters." },
    { w: "pona", tp: "mi pona e ni", en: "I fix this", wrong: ["I like this", "this is good", "I break this"], note: "<b>pona</b> as a verb: to fix or improve — to <i>make</i> something good." },
    { w: "sin", tp: "ona li toki sin", en: "they speak again", wrong: ["they speak first", "they are new here", "they talk a lot"], note: "<b>sin</b> after a verb: again, anew." },
    { w: "sama", tp: "mi sama sina", en: "I am like you", wrong: ["I am you", "you and I are one person", "I see you"], note: "<b>sama</b> bridges two things: same as, similar to." },
    { w: "mute", tp: "ni li pona mute", en: "this is very good", wrong: ["these are many good things", "this is a little good", "this is good again"], note: "<b>mute</b> after a quality: very. pona mute — very good." },
  ],
  u3: [
    { w: "kule", tp: "mi kule e ni", en: "I color this", wrong: ["I see the color", "this is colorful", "I like this color"], note: "<b>kule</b> as a verb: to color, to paint." },
    { w: "walo", tp: "ona li walo", en: "it is pale", wrong: ["it is dark", "it is clean", "it is bright yellow"], note: "<b>walo</b> is any light, washed-out shade — white through pale." },
    { w: "laso", tp: "ni li laso", en: "it's blue — or green, same word", wrong: ["it is light-colored", "it is red", "it is dark"], note: "<b>laso</b> spans blue <i>and</i> green — toki pona sees one cool color there." },
    { w: "kule", tp: "ijo kule li pona", en: "colorful things are good", wrong: ["the color is good", "things have colors", "good things are one color"], note: "as a modifier, <b>kule</b> means colorful." },
    { w: "ala", tp: "jan ala li toki", en: "nobody is talking", wrong: ["the person isn't talking", "people talk about nothing", "someone is quiet"], note: "<b>ala</b> right after a noun zeroes it out: jan ala — no person at all, nobody." },
    { w: "pimeja", tp: "pimeja li suli", en: "the darkness is vast", wrong: ["black is beautiful", "the darkness is small", "darkness is bad"], note: "<b>pimeja</b> as a noun: the dark itself — shadow, night, absence of light." },
  ],
  u4: [
    { w: "waso", tp: "pipi ni li waso", en: "this bug is a flyer", wrong: ["this bug is small", "this bird is a bug", "this bird eats bugs"], note: "<b>waso</b> stretches to any flying animal — a flying bug can be waso." },
    { w: "kala", tp: "soweli ni li kala", en: "this animal is a swimmer", wrong: ["this animal eats fish", "this fish is furry", "this animal is wet"], note: "<b>kala</b> is anything that lives and swims in water — a seal can be kala." },
    { w: "akesi", tp: "ona li akesi anu pipi?", en: "is it a reptile or a bug?", wrong: ["it is a reptile and a bug", "are reptiles bugs?", "which bug is it?"], note: "categories are fuzzy on purpose — for a crawling thing, akesi and pipi are both fair guesses." },
    { w: "kijetesantakalu", tp: "kijetesantakalu li soweli", en: "a raccoon-kin is a land animal", wrong: ["the raccoon is a joke", "weasels are birds", "the raccoon-kin is big"], note: "even the joke word nests inside <b>soweli</b> — the categories contain each other." },
    { w: "waso", tp: "ni li waso ala waso?", en: "is this a bird?", wrong: ["this is not a bird", "is the bird here?", "what bird is this?"], note: "verb ala verb — the yes/no question — works with nouns as descriptions too." },
  ],
  u5: [
    { w: "moku", tp: "mi moku e telo", en: "I drink water", wrong: ["I eat food", "I wash myself", "the water is drinkable"], note: "<b>moku</b> covers eating <i>and</i> drinking — one word for taking things in." },
    { w: "telo", tp: "mi telo e soweli", en: "I wash the animal", wrong: ["the animal drinks", "I give water to the animal", "the animal is wet"], note: "<b>telo</b> as a verb: to wash, to water." },
    { w: "suwi", tp: "soweli ni li suwi", en: "this animal is cute", wrong: ["this animal is tasty", "this animal is small", "this food is animal-shaped"], note: "<b>suwi</b> is sweet to the tongue <i>and</i> to the heart — cute." },
    { w: "moku", tp: "kili li moku pona", en: "fruit is good food", wrong: ["the fruit eats well", "vegetables taste better", "fruit is sweet"], note: "<b>moku</b> as a noun: food itself." },
    { w: "telo", tp: "telo suli li pona", en: "the sea is lovely", wrong: ["the sea is dangerous", "the water is deep", "the rain is nice"], note: "<b>telo suli</b> — the big water: the sea or a lake. Two small words, one new thing." },
  ],
  u6: [
    { w: "luka", tp: "mi luka e soweli", en: "I pet the animal", wrong: ["I hold five animals", "the animal has paws", "I wave at the animal"], note: "<b>luka</b> as a verb: to touch or handle." },
    { w: "pilin", tp: "mi pilin e ni", en: "I think this", wrong: ["I touch this", "this hurts me", "I hear this"], note: "<b>pilin</b> is inner sensing — to feel, and by extension to think." },
    { w: "noka", tp: "mi lukin e noka kasi", en: "I look at the base of the plant", wrong: ["I see the plant's leg", "the plant steps on me", "I look at the small plant"], note: "<b>noka</b> is the lower part of anything — the foot of a hill, the base of a plant." },
    { w: "lukin", tp: "lukin ona li laso", en: "their eyes are blue", wrong: ["they see blue things", "they look bluish", "their sight is cold"], note: "<b>lukin</b> as a noun: the eye itself." },
    { w: "kute", tp: "sina kute ala e mi", en: "you don't listen to me", wrong: ["you can't hear", "I don't listen to you", "you hear me"], note: "<b>kute e jan</b> — to listen to someone, to heed them." },
  ],
  u7: [
    { w: "kama", tp: "ona li kama suli", en: "it is getting big", wrong: ["it arrives big", "it is big", "it wants to be big"], note: "<b>kama</b> + a description: to become. kama suli — to grow." },
    { w: "kama", tp: "ona li kama moku", en: "they start to eat", wrong: ["they come to dinner", "they want to eat", "they can eat"], note: "<b>kama</b> as a preverb: to start to." },
    { w: "awen", tp: "mi awen toki", en: "I keep talking", wrong: ["I stop talking", "I wait to talk", "I talk quietly"], note: "<b>awen</b> as a preverb: to continue doing something." },
    { w: "lukin", tp: "mi lukin pali e ni", en: "I try to make this", wrong: ["I watch this being made", "I see the work", "I want to make this"], note: "<b>lukin</b> as a preverb: to try to. An old word gets a new job once preverbs exist." },
    { w: "tawa", tp: "ni li pona tawa mi", en: "I like this", wrong: ["this moves toward me", "I am good to this", "this makes me good"], note: "pona <b>tawa</b> mi — good from where I stand. This is how toki pona says to like." },
    { w: "tawa", tp: "mi lukin e telo tawa", en: "I watch the flowing water", wrong: ["I see the water leave", "I watch water go to it", "the water sees me move"], note: "<b>tawa</b> as a modifier: moving. telo tawa — flowing water." },
    { w: "pana", tp: "ona li pana e moku tawa mi", en: "they give me food", wrong: ["they take food from me", "I give them food", "they eat my food"], note: "<b>pana</b> sends outward; tawa marks who receives." },
  ],
  u8: [
    { w: "sona", tp: "mi kama sona e toki pona", en: "I am learning toki pona", wrong: ["I know toki pona", "I came to talk simply", "I want to learn toki pona"], note: "<b>kama sona</b> — to come to know: to learn. It's this app's name: o kama sona!" },
    { w: "sona", tp: "mi sona toki", en: "I know how to talk", wrong: ["I know the language", "I talk wisely", "I am learning to talk"], note: "<b>sona</b> as a preverb: to know how to." },
    { w: "wawa", tp: "mi pilin wawa", en: "I feel energetic", wrong: ["I feel weak", "I think about power", "I am strong"], note: "pilin <b>wawa</b> — feeling full of strength or energy." },
    { w: "nasa", tp: "telo nasa li ike tawa mi", en: "I don't like alcohol", wrong: ["strange water is dangerous", "I hate swimming", "the silly water dislikes me"], note: "<b>telo nasa</b> — 'silly water' — is the word for alcohol." },
    { w: "lape", tp: "soweli lape li suwi", en: "a sleeping animal is cute", wrong: ["the animal sleeps sweetly", "sleepy animals are sweet-tasting", "the animal dreams"], note: "<b>lape</b> as a modifier: sleeping, at rest." },
    { w: "musi", tp: "toki musi ona li pona", en: "their joke is good", wrong: ["they speak playfully", "their art is good", "their game talks"], note: "<b>toki musi</b> — playful talk: a joke, a story, wordplay." },
    { w: "lape", tp: "o lape pona", en: "sleep well!", wrong: ["good sleep", "I sleep well", "the sleep is good"], note: "<b>o</b> + verb makes a wish or command — o lape pona is the toki pona goodnight." },
  ],
  u9: [
    { w: "suno", tp: "mi lukin e suno mun", en: "I look at the moonlight", wrong: ["I see the sun and the moon", "I look at the bright sun", "the moon is rising"], note: "<b>suno</b> is any light — suno mun, the moon's light." },
    { w: "kon", tp: "nimi ni li jo e kon", en: "this word carries meaning", wrong: ["this word has air", "this name is windy", "this word is invisible"], note: "<b>kon</b> — air, spirit, and the invisible essence of things: meaning." },
    { w: "seli", tp: "mi seli e moku", en: "I cook the food", wrong: ["the food is hot", "I eat warm food", "the fire eats the food"], note: "<b>seli</b> as a verb: to heat, to cook." },
    { w: "lete", tp: "mi moku e kala lete", en: "I eat raw fish", wrong: ["the fish is cold", "I eat frozen food", "the fish eats in the cold"], note: "<b>lete</b>: cold — and raw, uncooked. kala lete is basically sushi." },
    { w: "sewi", tp: "ni li ijo sewi", en: "this is a sacred thing", wrong: ["this thing is on the ground", "this is a low place", "this thing is falling"], note: "<b>sewi</b> reaches from 'up above' to the sacred and divine — context decides." },
    { w: "sike", tp: "mi tawa sike", en: "I go around in circles", wrong: ["I roll the ball", "I go far away", "the wheel turns"], note: "<b>sike</b> as a modifier: circling, looping. tawa sike — to go in circles." },
    { w: "anpa", tp: "ona li anpa e mi", en: "they defeated me", wrong: ["they are below me", "I fell down", "they bow to me"], note: "<b>anpa</b> as a verb: to bring low — to defeat or humble." },
  ],
  u10: [
    { w: "tomo", tp: "mi jo e tomo tawa", en: "I have a car", wrong: ["I go home", "my house is moving", "I have a room to go to"], note: "<b>tomo tawa</b> — a moving room: a car. The most famous toki pona compound." },
    { w: "kiwen", tp: "telo kiwen li lete", en: "ice is cold", wrong: ["the stone is wet", "hard water is heavy", "cold water hardens"], note: "<b>telo kiwen</b> — solid water: ice." },
    { w: "lipu", tp: "mi lukin e lipu sona", en: "I read a book", wrong: ["I watch the smart paper", "the book looks at me", "I write a book"], note: "<b>lipu sona</b> — a knowledge-paper: a book or document. lukin e lipu — to read." },
    { w: "len", tp: "mi len e ijo ni", en: "I hide this thing", wrong: ["I wear this", "this thing is cloth", "I fold the cloth"], note: "<b>len</b> as a verb: to cover — and so to hide." },
    { w: "poki", tp: "mi jo e poki pi telo seli", en: "I have a cup of hot water", wrong: ["my cup makes water hot", "I have hot water and a cup", "I heat the cup with water"], note: "<b>pi</b> groups telo seli into one idea: a container of (hot water)." },
    { w: "supa", tp: "supa lape mi li pona", en: "my bed is comfy", wrong: ["my table is asleep", "I sleep on the floor", "my sleep is good"], note: "<b>supa lape</b> — the sleeping surface: a bed." },
  ],
  u11: [
    { w: "nena", tp: "nena mi li lili", en: "my nose is small", wrong: ["the hill is small", "my nose is big", "I bumped something small"], note: "<b>nena</b> on a face is the nose — the bump that sticks out." },
    { w: "selo", tp: "selo kili li jelo", en: "the fruit's peel is yellow", wrong: ["the fruit is yellow inside", "the shell is a fruit", "the yellow fruit has skin"], note: "<b>selo</b>: any outer layer — peel, bark, shell, skin." },
    { w: "sinpin", tp: "sinpin ona li loje", en: "their face is red", wrong: ["their hair is red", "they painted the front", "their back is red"], note: "<b>sinpin</b> — a vertical face: a wall, a front, or the face on a person." },
    { w: "insa", tp: "insa mi li pilin ike", en: "my stomach hurts", wrong: ["the middle one is bad", "I am inside something bad", "my inner thoughts are dark"], note: "<b>insa</b> — the inside; of a body, the belly." },
    { w: "poka", tp: "jan poka li toki tawa mi", en: "the person next to me talks to me", wrong: ["someone talks behind me", "my side hurts", "a stranger walks beside me"], note: "<b>jan poka</b> — the person at your side: a neighbor, company." },
    { w: "lupa", tp: "lupa tomo li suli", en: "the door of the house is big", wrong: ["the house's hole is deep", "the window is small", "the house is open"], note: "<b>lupa</b>: a hole — and so a door or window, the holes a building wears." },
    { w: "monsi", tp: "monsi tomo li pimeja", en: "the back of the house is dark", wrong: ["the front of the house is dark", "the house is dark inside", "the dark house is behind"], note: "<b>monsi</b> — the back of something, and the space behind it." },
  ],
  u12: [
    { w: "lawa", tp: "ona li lawa e kulupu", en: "they lead the group", wrong: ["their head is big", "the group controls them", "they count the people"], note: "<b>lawa</b> as a verb: to lead, to govern." },
    { w: "mama", tp: "mi mama e kasi mi", en: "I take care of my plants", wrong: ["my parent is a plant", "the plant is my mother", "I planted my garden"], note: "<b>mama</b> as a verb: to parent — to raise, nurture, look after." },
    { w: "esun", tp: "mi esun e pan", en: "I buy bread", wrong: ["I bake bread", "I eat at the market", "the bread is expensive"], note: "<b>esun</b> covers the whole exchange — buy, sell, trade. Context picks the direction." },
    { w: "moli", tp: "ilo mi li moli", en: "my machine is dead", wrong: ["my tool is dangerous", "my machine is asleep", "my machine kills"], note: "<b>moli</b> stretches to 'broken beyond repair' — a dead phone is moli." },
    { w: "pakala", tp: "pakala a!", en: "dang it!", wrong: ["so broken!", "be careful!", "it hurts!"], note: "<b>pakala</b> on its own is the all-purpose oops — toki pona's polite swear." },
    { w: "utala", tp: "utala musi li pona tawa mi", en: "I like sports", wrong: ["I like fighting", "war games are fun", "I am good at fighting"], note: "<b>utala musi</b> — playful struggle: a game, sport, or competition." },
  ],
  u13: [
    { w: "tenpo", tp: "tenpo suno ni li pona", en: "today is good", wrong: ["the sun is out now", "this hour is sunny", "the daylight is nice"], note: "<b>tenpo suno</b> — the sun-time: a day. tenpo suno ni — today." },
    { w: "ale", tp: "tenpo ale la sina pona tawa mi", en: "I always like you", wrong: ["sometimes you are good to me", "you like me all the time", "forever is good for us"], note: "<b>tenpo ale</b> — all time: always. la hangs it over the whole sentence." },
    { w: "wan", tp: "kulupu li wan", en: "the group is united", wrong: ["the group is number one", "there is one group", "the group is alone"], note: "<b>wan</b> beyond counting: united, joined as one." },
    { w: "tu", tp: "mi tu e pan", en: "I split the bread", wrong: ["I have two loaves", "the bread is second", "I eat two pieces"], note: "<b>tu</b> as a verb: to split in two." },
    { w: "taso", tp: "sina taso li sona e ni", en: "only you know this", wrong: ["but you know this", "you know only this", "everyone knows this"], note: "<b>taso</b> after a noun: only that one. sina taso — only you. (And note the li — a modified sina takes it.)" },
    { w: "kin", tp: "mi kin li wile moku", en: "I want to eat too", wrong: ["I really need food", "only I want to eat", "I want more food"], note: "<b>kin</b> — also, too. mi kin — me too." },
    { w: "nanpa", tp: "ona li nanpa wan", en: "they are number one", wrong: ["they are alone", "they count things", "there is one of them"], note: "<b>nanpa</b> + a number makes ordinals: nanpa wan — first, the best." },
    { w: "tenpo", tp: "tenpo pimeja la soweli li kalama", en: "at night, the animals make noise", wrong: ["dark times make animals loud", "the animals darken the night", "at night the animals sleep"], note: "<b>tenpo pimeja</b> — the dark-time: night." },
  ],
  u14: [
    { w: "lon", tp: "toki sina li lon", en: "what you say is true", wrong: ["you are lying", "you are here", "you talk in place"], note: "<b>lon</b> beyond place: real, true. toki li lon — the talk is true." },
    { w: "sitelen", tp: "mi lukin e sitelen tawa", en: "I watch a movie", wrong: ["I see moving writing", "I draw a journey", "I read while walking"], note: "<b>sitelen tawa</b> — moving pictures: a movie or video." },
    { w: "tan", tp: "mi pilin pona tan sina", en: "I feel good because of you", wrong: ["I feel like you", "I feel your feelings", "you make me sick"], note: "<b>tan</b> gives the reason — because of, thanks to." },
    { w: "ante", tp: "mi ante e nimi mi", en: "I change my name", wrong: ["my name is different", "I have two names", "someone renamed me"], note: "<b>ante</b> as a verb: to change, to alter." },
    { w: "open", tp: "mi open e ilo", en: "I turn on the machine", wrong: ["I begin working", "the machine starts me", "I break the machine open"], note: "<b>open</b>: to open, begin — and to switch on." },
    { w: "pini", tp: "mi pini e pali mi", en: "I finish my work", wrong: ["I start my work", "my work ends me", "I stop working forever"], note: "<b>pini</b>: to end, finish — and to switch off." },
    { w: "weka", tp: "o weka e ijo jaki ni", en: "get rid of this gross thing!", wrong: ["this thing is far away", "the gross thing left", "throw it at me"], note: "<b>weka</b> as a verb: to remove, to get rid of." },
    { w: "nasin", tp: "nasin ni li pona tawa mi", en: "I like this method", wrong: ["this road leads to me", "I walk this path", "this street is mine"], note: "<b>nasin</b> — a road, and a way of doing things: a method, a custom." },
    { w: "kepeken", tp: "mi sitelen kepeken ilo", en: "I write with a tool", wrong: ["I draw the tool", "the tool writes", "I use written tools"], note: "<b>kepeken</b> follows the verb with no e — it marks the tool being used." },
  ],
};

// The distinct words a unit's stretch bank exercises (for the learned panel).
export function stretchWords(unitId) {
  return [...new Set((STRETCHES[unitId] || []).map((s) => s.w))];
}

// Build the capstone quiz for a unit: the whole stretch bank, shuffled, served
// as a mix of translate questions and build-the-sentence tiles so the new
// senses get both recognized and produced. Punctuated or very short sentences
// stay translate-only (a one-word build is no build at all).
export function buildStretch(unitId) {
  const bank = STRETCHES[unitId] || [];
  return shuffle([...bank]).map((item, i) => {
    const tokens = item.tp.split(" ");
    const buildable = tokens.length >= 3 && !/[!?]/.test(item.tp);
    if (buildable && i % 3 === 1) {
      const traps = ["e", "li", "la", "pi"].filter((x) => !tokens.includes(x));
      return {
        type: "build",
        tp: item.tp,
        en: item.en,
        note: item.note,
        correct: item.tp,
        alts: item.alt || [],
        tiles: shuffle([...tokens, sample(traps, 1)[0]]),
      };
    }
    return {
      type: "phrase",
      tp: item.tp,
      en: item.en,
      note: item.note,
      correct: item.en,
      options: shuffle([item.en, ...item.wrong]),
    };
  });
}

/* ---------------- review ---------------- */
// The "daily warm-up": a short quiz drawn entirely from whatever's already
// unlocked. No new words are introduced and finishing it doesn't mark any
// lesson complete — it's just spaced repetition over what you've met so far.
// Same recipe and question shapes as buildLesson (recognition → match →
// production → phrases → grammar), just resampled from the whole unlocked
// pool instead of one lesson's focus words, so the UI can render it with the
// exact same components. Distractors are drawn from the pool first — a
// distractor you haven't unlocked yet is a free giveaway — falling back to
// the full vocabulary only when the pool is too small to fill the options.
export function buildReview(completed = {}, { audio = false } = {}) {
  const pool = unlockedWords(completed);
  if (!pool.length) return [];

  const allWords = Object.keys(WORDS);
  const glossOf = (w) => WORDS[w].g;

  const wordDistractors = (w) => {
    const out = sample(pool, 3, [w]);
    if (out.length < 3) {
      out.push(...sample(allWords, 3 - out.length, [w, ...out]));
    }
    return out;
  };
  const glossDistractors = (w) => {
    const seenG = new Set([glossOf(w)]);
    const out = [];
    for (const x of [...shuffle(pool), ...shuffle(allWords)]) {
      if (out.length >= 3) break;
      const g = glossOf(x);
      if (seenG.has(g)) continue;
      seenG.add(g);
      out.push(g);
    }
    return out;
  };

  const meaningQ = (w) => ({
    type: "meaning",
    word: w,
    correct: glossOf(w),
    options: shuffle([glossOf(w), ...glossDistractors(w)]),
  });
  const wordQ = (w) => ({
    type: "word",
    word: w,
    correct: w,
    options: shuffle([w, ...wordDistractors(w)]),
  });

  // Recognition: up to three random pool words, by eye or (with audio) by ear.
  const recogWords = sample(pool, Math.min(3, pool.length));
  let meaningQs = recogWords.map(meaningQ);
  let listenQs = [];
  if (audio) {
    const byEar = new Set(sample(recogWords, Math.min(2, recogWords.length)));
    listenQs = [...byEar].map((w) => ({ ...meaningQ(w), type: "listen" }));
    meaningQs = meaningQs.filter((q) => !byEar.has(q.word));
  }

  // Match: one 4-pair round, skipped entirely if the pool can't fill it.
  const matchQs = [];
  if (pool.length >= 4) {
    const mp = sample(pool, 4);
    matchQs.push({
      type: "match",
      pairs: mp.map((w) => ({ tp: w, en: glossOf(w) })),
    });
  }

  // Production: up to three, preferring words recognition didn't already use
  // (falls back to reusing them once the pool is too small to keep them separate).
  const prodCount = Math.min(3, pool.length);
  let prodWords = sample(pool, prodCount, recogWords);
  if (prodWords.length < prodCount) {
    prodWords.push(...sample(pool, prodCount - prodWords.length, prodWords));
  }
  const wordQs = prodWords.map(wordQ);

  // Phrases: up to two from whatever's unlocked; with audio, one becomes a
  // "pick what you heard" listening question exactly like buildLesson's.
  const phrasePool = shuffle(unlockedPhrases(pool));
  const chosenPhrases = phrasePool.slice(0, Math.min(2, phrasePool.length));
  let phraseQs = chosenPhrases.map((p) => ({
    type: "phrase",
    tp: p.tp,
    correct: p.en,
    options: shuffle([p.en, ...p.wrong]),
    hasParticle: /\b(li|e|la|pi)\b/.test(p.tp),
  }));
  if (audio) {
    const li = chosenPhrases.findIndex((p) => p.tp.split(" ").length >= 3);
    if (li >= 0) {
      const p = chosenPhrases[li];
      phraseQs[li] = {
        type: "listenPhrase",
        tp: p.tp,
        en: p.en,
        correct: p.tp,
        options: shuffle([p.tp, ...phraseMishearings(p.tp, pool)]),
      };
    }
  }

  // Grammar: at most one spot + one transform. Gated on the pattern's own
  // lesson actually being completed (not just "reachable in order", the way
  // buildGrammarDrills gates it) — review only revisits grammar you've
  // genuinely finished, plus having the words it needs in the pool.
  const grammarQs = [];
  const spots = GRAMMAR_CHECKS.filter((c) => {
    const pattern = PATTERNS.find((p) => p.id === c.pat);
    return pattern && completed[pattern.at] && c.needs.every((w) => pool.includes(w));
  });
  if (spots.length) {
    const c = sample(spots, 1)[0];
    grammarQs.push({
      type: "spot",
      tp: c.tp,
      ok: c.ok,
      why: c.why,
      correct: c.ok ? "pona" : "ike",
    });
  }
  const transforms = TRANSFORMS.filter((t) => {
    const pattern = PATTERNS.find((p) => p.id === t.pat);
    return pattern && completed[pattern.at] && t.needs.every((w) => pool.includes(w));
  });
  if (transforms.length) {
    const t = sample(transforms, 1)[0];
    grammarQs.push({
      type: "transform",
      from: t.from,
      op: t.op,
      correct: t.to,
      options: shuffle([t.to, ...t.wrong]),
    });
  }

  return [
    ...shuffle([...meaningQs, ...listenQs]),
    ...matchQs,
    ...shuffle(wordQs),
    ...phraseQs,
    ...shuffle(grammarQs).slice(0, 2),
  ];
}

/* ---------------- glossary ---------------- */
// The shortest example phrase that uses a word — for the glossary, where a
// small real sentence beats no sentence, even if the reader hasn't unlocked
// every word in it yet. Null for words the phrase bank never uses.
export const shortestPhraseWith = (word) => {
  let best = null;
  for (const p of PHRASES) {
    if (!p.tp.split(" ").includes(word)) continue;
    if (!best || p.tp.split(" ").length < best.tp.split(" ").length) best = p;
  }
  return best ? { tp: best.tp, en: best.en } : null;
};

// The full vocabulary as a reference: one section per unit (curriculum
// order), plus a final section for the particles. Every entry carries the
// word, its short gloss, the long note, and an example phrase when one
// exists. Particle entries are derived from their grammar PATTERNS — each
// particle's pattern id is the particle itself, its gloss is the half of
// the pattern title after the "—", and its note is the pattern's rule.
export function glossarySections() {
  const unitSections = UNITS.map((u, i) => ({
    id: u.id,
    title: u.title,
    gloss: u.gloss,
    label: `unit ${i + 1}`,
    entries: u.words.map((w) => ({
      word: w,
      gloss: WORDS[w].g,
      note: WORDS[w].n,
      example: shortestPhraseWith(w),
    })),
  }));
  const particleEntries = PARTICLES.map((p) => {
    const pat = PATTERNS.find((x) => x.id === p);
    return {
      word: p,
      gloss: pat ? pat.title.split("—").pop().trim() : "particle",
      note: pat ? pat.rule : PARTICLE_NOTE,
      example: (pat && pat.examples[0]) || shortestPhraseWith(p),
    };
  });
  return [
    ...unitSections,
    {
      id: "particles",
      title: "nimi lili",
      gloss: "the particles",
      label: "grammar",
      entries: particleEntries,
    },
  ];
}

// Filter glossary sections by a search query, matching the toki pona word,
// the short gloss, or the long note (HTML stripped). Word and gloss match by
// substring ("ona" should find sona and pona too), but notes only match at a
// word boundary — nearly every note mentions "toki pona", so a substring
// match there would return half the glossary for "ona", kijetesantakalu
// included. Empty sections drop out; an empty query returns everything
// untouched.
export function filterGlossary(sections, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return sections;
  const plain = (html) => html.replace(/<[^>]+>/g, "").toLowerCase();
  const noteRe = new RegExp(`\\b${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`);
  return sections
    .map((s) => ({
      ...s,
      entries: s.entries.filter(
        (e) =>
          e.word.includes(q) ||
          e.gloss.toLowerCase().includes(q) ||
          noteRe.test(plain(e.note))
      ),
    }))
    .filter((s) => s.entries.length > 0);
}

/* ---------------- cross-device carry ----------------
   Progress lives in Firestore at tokipona/{userId}, where userId is the
   anonymous per-device id. "Carrying" progress is just moving that id to
   another device: surface it as a copyable code, and accept a pasted code to
   re-bind this device to it. This normalizes a pasted code, returning the
   cleaned id or null if it doesn't look like one (a uuid or the
   `${timestamp}-${rand}` fallback shape both fit). */
export function normalizeRecoveryCode(raw) {
  const code = String(raw || "").trim();
  return /^[A-Za-z0-9-]{8,}$/.test(code) ? code : null;
}
